import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  CreditCard, ShieldCheck, Sparkles, CheckCircle, Flame, ArrowRight, 
  CornerDownRight, Zap, Info, Clock, Check, X, Shield, Cpu, 
  RefreshCw, Landmark, Database, HelpCircle, FileText, Award, 
  Layers, Globe, Mail, ListFilter, Play, Trash2, ChevronRight, 
  Activity, Download, Plus, BarChart2, MessageSquare, BookOpen, 
  Copy, FilePlus, AlertTriangle, Terminal, Phone, Landmark as BankIcon
} from 'lucide-react';
import { Star, Crown, Gem, Hexagon } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { auth } from '../lib/firebase';

// Tide Payment Links placeholders
const TIDE_PAYMENT_LINK_7_DAY_SPRINT = "https://pay.tide.co/8eb0f8a7-58d7-45d5-a0ca-b1d9bcfa6ae9";
const TIDE_PAYMENT_LINK_15_DAY_EXPANSION = "https://pay.tide.co/9a191eb9-d27a-4435-aedc-1753dcc081e2";
const TIDE_PAYMENT_LINK_30_DAY_MASTERY = "https://pay.tide.co/0643bdff-6b15-492d-8598-4e93f58f5987";
// Tide Package Payment Links
const TIDE_PAYMENT_LINK_BRONZE_PACKAGE = "https://pay.tide.co/322d3c58-f41f-401a-be62-19628b55ea1b";
const TIDE_PAYMENT_LINK_SILVER_PACKAGE = "https://pay.tide.co/e9d6fcf5-9536-4639-b2bd-e7388783a763";
const TIDE_PAYMENT_LINK_GOLD_DEPOSIT = "https://pay.tide.co/7a45e241-0ee0-4697-b125-1fd511b77be9";
const TIDE_PAYMENT_LINK_STARTER_MAINTENANCE = "https://pay.tide.co/af78b35d-91f2-4436-b13e-e8300f0d8804";
const TIDE_PAYMENT_LINK_PREMIER_MAINTENANCE = "https://pay.tide.co/485e2094-d41e-4700-a207-7d8e4775a467";
const TIDE_PAYMENT_LINK_PRO_MAINTENANCE = "https://pay.tide.co/7cd903a6-64b6-4956-8523-0e21f2cba561";

// Helper for sound effects
const playNexoraXSound = (freq = 440, duration = 0.2, type: OscillatorType = "sine") => {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    osc.type = type;
    osc.frequency.setValueAtTime(freq, audioCtx.currentTime);
    
    gainNode.gain.setValueAtTime(0.15, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);
    
    osc.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    osc.start();
    osc.stop(audioCtx.currentTime + duration);
  } catch (e) {}
};

// Helper to render styled Lucide icons for package tiers
const renderTierIcon = (iconName: string) => {
  switch (iconName) {
    case 'bronze-shield':
      return <Shield className="w-8 h-8 text-[#cd7f32] filter drop-shadow-[0_0_8px_rgba(205,127,50,0.5)] shrink-0" />;
    case 'silver-star':
      return <Star className="w-8 h-8 text-[#cbd5e1] filter drop-shadow-[0_0_8px_rgba(203,213,225,0.5)] shrink-0" />;
    case 'gold-crown':
      return <Crown className="w-8 h-8 text-[#ffd700] filter drop-shadow-[0_0_8px_rgba(255,215,0,0.5)] shrink-0" />;
    case 'cyan-diamond':
      return <Gem className="w-8 h-8 text-[#00ffff] filter drop-shadow-[0_0_8px_rgba(0,255,255,0.5)] shrink-0" />;
    case 'platinum-hexagon':
      return <Hexagon className="w-8 h-8 text-[#e5e4e2] filter drop-shadow-[0_0_8px_rgba(229,228,226,0.5)] shrink-0" />;
    case 'obsidian-diamond':
      return <Sparkles className="w-8 h-8 text-[#a855f7] filter drop-shadow-[0_0_8px_rgba(168,85,247,0.5)] shrink-0" />;
    default:
      return <Shield className="w-8 h-8 text-cyan-400 shrink-0" />;
  }
};


// ---------------------------------------------------------------------
// STATIC SPEC DETAIL TOOLTIP DEFINITIONS
// ---------------------------------------------------------------------
const featureDetailsMap: { [key: string]: string } = {
  'Standard Hosting': 'High-availability container hosting for your localized automated agent workers.',
  'Neural Chat': 'Basic chat processing node accessing high-speed cognitive language models.',
  'Automation': 'Ability to set up sequential system triggers to run automated business workflows.',
  'Social Bridge': 'Multi-channel API connector targeting external media profiles with high accuracy.',
  'Experience Cloud': 'Client-facing UI node allowing external operators to securely verify active deliverables.',
  "Saby's Tier": "Saby's premium closing and sales-qualified outreach sequences fully configured.",
  'ROI Simulator': 'Advanced financial projection deck computing return on automation index scores instantly.',
  'Closing Bot': 'Autonomous conversational closer closing high-velocity incoming digital leads.',
  'Neural Analytics': 'Real-time D3 analytics detailing active node efficiency and operational logs.',
  'AI Avatar': 'Voice and video-synthesized digital representative representing your brand 24/7.',
  'ML Studio': 'Full Machine Learning model playground to fine-tune unique custom agents.',
  'Agent Engines': 'Permission layers and multi-agent workspace threads communicating concurrently.',
  'Custom VM Nodes': 'Bespoke computational partitions running on dedicated Hetzner cloud slices.',
  'Enterprise Operations': 'Custom B2B setups, enterprise security compliance, and custom SLA response guarantees.',
  'Custom Enterprise Capabilities': 'Complete custom code modules and secure database environments.'
};

const MONTHLY_PAYMENT_UPLIFT = 0.20;

const calculateMonthlyPlanPrice = (annualPrice: number) => {
  return Number(((annualPrice * (1 + MONTHLY_PAYMENT_UPLIFT)) / 12).toFixed(2));
};

const formatGbp = (amount: number) => {
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2
  });
};

export function FeatureTooltip({ isHovered, feature }: { isHovered: boolean; feature: string }) {
  return (
    <AnimatePresence>
      {isHovered && (
        <motion.div
          initial={{ opacity: 0, y: 5, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 5, scale: 0.95 }}
          transition={{ duration: 0.12 }}
          className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-slate-950/98 border border-cyan-500/30 rounded-xl shadow-[0_12px_24px_rgba(0,0,0,0.85)] backdrop-blur-md z-[99] text-left pointer-events-none"
        >
          <div className="flex items-center gap-1.5 mb-1.5 text-cyan-400 font-sans font-bold text-[9px] tracking-wider uppercase border-b border-white/5 pb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_cyan]" />
            <span>NEXORAX MATRIX SPEC</span>
          </div>
          <p className="text-[10px] font-mono text-slate-300 leading-normal normal-case font-medium">
            {featureDetailsMap[feature] || 'Proprietary enterprise specification of the NexoraX automation engine.'}
          </p>
          <div className="absolute top-full right-3 w-2.5 h-2.5 bg-slate-950 border-r border-b border-cyan-500/20 rotate-45" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function PaymentTiers() {
  const verifiedPreamble = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";
  const { triggerToast } = useToast();

  // ----------------------------------------------------
  // CORE MANAGEMENT STATES
  // ----------------------------------------------------
  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [selectedPlan, setSelectedPlan] = useState<string>('t3'); // Gold by default
  const [driverQuoteName, setDriverQuoteName] = useState('');
  const [driverQuoteBusiness, setDriverQuoteBusiness] = useState('');
  const [driverQuoteEmail, setDriverQuoteEmail] = useState('');
  const [driverQuotePhone, setDriverQuotePhone] = useState('');
  const [driverQuoteBusinessType, setDriverQuoteBusinessType] = useState('Courier');
  const [driverQuoteVehicles, setDriverQuoteVehicles] = useState('');
  const [driverQuoteFeatures, setDriverQuoteFeatures] = useState<string[]>([]);
  const [driverQuoteMessage, setDriverQuoteMessage] = useState('');
  const [driverQuoteSubmitting, setDriverQuoteSubmitting] = useState(false);
  const [driverQuoteSuccess, setDriverQuoteSuccess] = useState(false);
  const [driverQuoteError, setDriverQuoteError] = useState<string | null>(null);
  const [tiersSearch, setTiersSearch] = useState<string>('');
  const [hoveredFeatureKey, setHoveredFeatureKey] = useState<string | null>(null);

  // Required checkout checkboxes states
  const [chkPlan, setChkPlan] = useState<boolean>(false);
  const [chkMaint, setChkMaint] = useState<boolean>(false);
  const [chkMaintStop, setChkMaintStop] = useState<boolean>(false);
  const [chkVat, setChkVat] = useState<boolean>(false);
  const [chkComm, setChkComm] = useState<boolean>(false);
  const [chkDifferent, setChkDifferent] = useState<boolean>(false);
  const [chkAgree, setChkAgree] = useState<boolean>(false);

  // Invoice request terms checkboxes
  const [chkInvoiceA, setChkInvoiceA] = useState<boolean>(false);
  const [chkInvoiceB, setChkInvoiceB] = useState<boolean>(false);

  // Staged payment terms checkbox
  const [chkStaged, setChkStaged] = useState<boolean>(false);

  // VIP Lifetime access terms checkbox
  const [chkVip, setChkVip] = useState<boolean>(false);

  // Show full policy view flags
  const [showFullRefundModal, setShowFullRefundModal] = useState<boolean>(false);
  const [showFullCancelModal, setShowFullCancelModal] = useState<boolean>(false);

  // Admin-only Customizable Terms Manager States with LocalStorage support
  const [adminRefundPolicyLink, setAdminRefundPolicyLink] = useState<string>(() => localStorage.getItem('nx_adminRefundPolicyLink') || "/refund-policy");
  const [adminCancellationPolicyLink, setAdminCancellationPolicyLink] = useState<string>(() => localStorage.getItem('nx_adminCancellationPolicyLink') || "/cancellation-policy");
  const [adminTermsOfServiceLink, setAdminTermsOfServiceLink] = useState<string>(() => localStorage.getItem('nx_adminTermsOfServiceLink') || "/terms-of-service");
  const [adminPrivacyPolicyLink, setAdminPrivacyPolicyLink] = useState<string>(() => localStorage.getItem('nx_adminPrivacyPolicyLink') || "/privacy-policy");
  
  const [adminMaintenanceFeeWording, setAdminMaintenanceFeeWording] = useState<string>(() => localStorage.getItem('nx_adminMaintenanceFeeWording') || 
    "I understand that the 10% Maintenance & Hosting Fee applies while my active NexoraX-hosted service, managed infrastructure, AI automation, website system, or platform workflow remains active, and stops upon cancellation or expiration of active hosted services."
  );
  const [adminCommunityAllocationWording, setAdminCommunityAllocationWording] = useState<string>(() => localStorage.getItem('nx_adminCommunityAllocationWording') || 
    "NexoraX may allocate 3% of eligible net receipts to approved community or charitable initiatives, split equally as 1% to each of three nominated causes."
  );
  const [adminVatWording, setAdminVatWording] = useState<string>(() => localStorage.getItem('nx_adminVatWording') || 
    "UK VAT is only charged if NexoraX is VAT-registered or required to register. VAT registration may be required if VAT-taxable turnover exceeds £90,000 in any rolling 12-month period, or if taxable turnover is expected to exceed £90,000 in the next 30 days alone. Businesses may also voluntarily register for VAT below the threshold."
  );
  const [adminStagedWording, setAdminStagedWording] = useState<string>(() => localStorage.getItem('nx_adminStagedWording') || 
    "Staged payment options require written approval and may be subject to a separate written agreement."
  );
  const [adminVipWording, setAdminVipWording] = useState<string>(() => localStorage.getItem('nx_adminVipWording') || 
    "VIP & Enterprise Access is subject to approval, agreed scope, onboarding terms, service availability, acceptable use, and any ongoing managed service terms."
  );
  const [adminCancellationRouting, setAdminCancellationRouting] = useState<string>(() => localStorage.getItem('nx_adminCancellationRouting') || "support@nexoraxdigital.co.uk");
  
  // UK VAT Threshold Regulation States (HMRC threshold £90,000 as of 2024/2026)
  const [adminRollingTurnover, setAdminRollingTurnover] = useState<number>(() => Number(localStorage.getItem('adminRollingTurnover') || "125000"));
  const [adminSingleMonthExpectedTurnover, setAdminSingleMonthExpectedTurnover] = useState<number>(() => Number(localStorage.getItem('adminSingleMonthExpectedTurnover') || "15000"));
  
  // Additional terms and policy summary manager states requested by user
  const [adminInvoiceWordingA, setAdminInvoiceWordingA] = useState<string>(() => localStorage.getItem('nx_adminInvoiceWordingA') || 
    "I understand that requesting an invoice does not activate service access until payment is confirmed or manually approved by NexoraX."
  );
  const [adminInvoiceWordingB, setAdminInvoiceWordingB] = useState<string>(() => localStorage.getItem('nx_adminInvoiceWordingB') || 
    "I understand that bank transfer instructions, where applicable, are sent securely by invoice and are not displayed publicly on the website."
  );
  const [adminRefundPolicySummary, setAdminRefundPolicySummary] = useState<string>(() => localStorage.getItem('nx_adminRefundPolicySummary') || 
    "Refund eligibility depends on the product or service purchased, the amount of work already completed, and whether access or deployment has already started."
  );
  const [adminCancellationPolicySummary, setAdminCancellationPolicySummary] = useState<string>(() => localStorage.getItem('nx_adminCancellationPolicySummary') || 
    "Clients can request cancellation of active NexoraX services. Access to hosted systems, automation, workflows, and managed infrastructure may end when the active service terminates."
  );
  const [adminGeneralTermsWording, setAdminGeneralTermsWording] = useState<string>(() => localStorage.getItem('nx_adminGeneralTermsWording') || 
    "I agree to the NexoraX"
  );
  
  // Terms agreement and VIP maintenance fee variables
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [vipMaintOption, setVipMaintOption] = useState<'INCLUDED' | 'ANNUAL_10' | 'MONTHLY_10_ACTIVE' | 'CUSTOM_ENTERPRISE'>('CUSTOM_ENTERPRISE');

  // Admin Configurable Required Disclosure fields for Community Allocation
  const [adminBeneficiary1, setAdminBeneficiary1] = useState<string>("Dunfermline Area Community Trust");
  const [adminPercent1, setAdminPercent1] = useState<number>(1);
  const [adminBeneficiary2, setAdminBeneficiary2] = useState<string>("Global Relief Fund");
  const [adminPercent2, setAdminPercent2] = useState<number>(1);
  const [adminBeneficiary3, setAdminBeneficiary3] = useState<string>("UK Social Inclusion Foundation");
  const [adminPercent3, setAdminPercent3] = useState<number>(1);
  const [adminCalcBasis, setAdminCalcBasis] = useState<string>("eligible net receipts");
  const [adminExcludeVAT, setAdminExcludeVAT] = useState<string>("Yes");
  const [adminExcludeRefunds, setAdminExcludeRefunds] = useState<string>("Yes");
  const [adminTimePeriod, setAdminTimePeriod] = useState<string>("Fiscal Year 2026-2027");
  const [adminCap, setAdminCap] = useState<string>("None");
  const [adminPublicDisclosure, setAdminPublicDisclosure] = useState<boolean>(true);

  // 1. 60-Second Preview Access States
  const [previewActive, setPreviewActive] = useState<boolean>(true);
  const [previewTimeLeft, setPreviewTimeLeft] = useState<number>(60);
  const [previewExpired, setPreviewExpired] = useState<boolean>(false);
  const hasSessionAccessClearance = () => {
    if (typeof window === 'undefined') return false;
    return (
      sessionStorage.getItem('nexorax_mfa_verified') === 'true' ||
      localStorage.getItem('nexorax_mfa_verified') === 'true' ||
      !!sessionStorage.getItem('nexorax_bypassed_user_email') ||
      !!localStorage.getItem('nexorax_bypassed_user_email')
    );
  };

  const [bypassGate, setBypassGate] = useState<boolean>(() => hasSessionAccessClearance());

  useEffect(() => {
    const unlockFromSession = () => {
      if (!hasSessionAccessClearance()) return;
      setBypassGate(true);
      setPreviewExpired(false);
      setPreviewActive(false);
      setPreviewTimeLeft(0);
    };

    unlockFromSession();
    const accessSyncTimer = window.setInterval(unlockFromSession, 500);
    window.addEventListener('mfa-verified', unlockFromSession);
    window.addEventListener('storage', unlockFromSession);
    window.addEventListener('focus', unlockFromSession);

    return () => {
      window.clearInterval(accessSyncTimer);
      window.removeEventListener('mfa-verified', unlockFromSession);
      window.removeEventListener('storage', unlockFromSession);
      window.removeEventListener('focus', unlockFromSession);
    };
  }, []);

  useEffect(() => {
    const handleDriverQuoteTrigger = () => {
      setTimeout(() => {
        document.getElementById('driver-fleet-quote')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    };

    window.addEventListener('nexorax-trigger-driver-quote', handleDriverQuoteTrigger);
    return () => {
      window.removeEventListener('nexorax-trigger-driver-quote', handleDriverQuoteTrigger);
    };
  }, []);

  // 2. Checkout & Invoice States
  const [paymentPortalTab, setPaymentPortalTab] = useState<'CHECKOUT' | 'INVOICE' | 'CONTACT'>('CHECKOUT');
  const [invoiceCompanyName, setInvoiceCompanyName] = useState<string>('');
  const [invoiceEmail, setInvoiceEmail] = useState<string>('');
  const [invoiceAddress, setInvoiceAddress] = useState<string>('');
  const [invoiceReference, setInvoiceReference] = useState<string>('');
  const [invoiceTermsAccepted, setInvoiceTermsAccepted] = useState<boolean>(false);

  // 3. Client Dashboard Metrics State
  const [queriesLeft, setQueriesLeft] = useState<number>(4476);
  const [queriesMax, setQueriesMax] = useState<number>(5000);
  const [activeClientPlan, setActiveClientPlan] = useState<string>('GOLD');
  const [clientCycle, setClientCycle] = useState<'Monthly' | 'Yearly'>('Monthly');

  // 4. Admin Interactive Controls
  const [isAdminMode, setIsAdminMode] = useState<boolean>(false);
  
  // Dynamic pricing states managed by Admin
  const [adminPrices, setAdminPrices] = useState<Record<string, number>>({
    bronze: 499,
    silver: 999,
    gold: 2499,
    diamond: 4999,
    platinum: 9999,
    black_diamond: 14999,
    vip_lifetime: 0
  });

  const [adminLimits, setAdminLimits] = useState<Record<string, { maxAgents: number; maxTasks: number }>>({
    bronze: { maxAgents: 2, maxTasks: 1000 },
    silver: { maxAgents: 10, maxTasks: 5000 },
    gold: { maxAgents: 35, maxTasks: 25000 },
    diamond: { maxAgents: 120, maxTasks: 150000 },
    platinum: { maxAgents: 350, maxTasks: 1000000 },
    black_diamond: { maxAgents: 999, maxTasks: 999999 }
  });

  // Client billing simulation statements log
  const [simulatedInvoices, setSimulatedInvoices] = useState<Array<{ id: string; date: string; amount: string; status: 'PAID' | 'PENDING' }>>([
    { id: 'NX-2026-059', date: '13 May 2026', amount: '£2,499.00', status: 'PAID' },
    { id: 'NX-2026-042', date: '13 Apr 2026', amount: '£2,499.00', status: 'PAID' },
    { id: 'NX-2026-021', date: '13 Mar 2026', amount: '£2,499.00', status: 'PAID' }
  ]);

  const [selectedQrInfo, setSelectedQrInfo] = useState<{
    title: string;
    price: number;
    image: string;
    url?: string;
  } | null>(null);

  // NexoraX Outreach Script Generator
  const [candidateName, setCandidateName] = useState<string>('');
  const [targetAgency, setTargetAgency] = useState<string>('');
  const [outreachTier, setOutreachTier] = useState<string>('Gold Block');
  const [outreachEmail, setOutreachEmail] = useState<string>('');

  // ----------------------------------------------------
  // PREVIEW ACCESS GATE TIMING TRIGGER
  // ----------------------------------------------------
  const routeToLoginForPricing = async (intent: string, planId = selectedPlan) => {
    try {
      await fetch("/api/access-gate/release", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent, planId })
      });
    } catch (err) {
      console.warn("Access gate release failed:", err);
    }

    const params = new URLSearchParams({
      intent,
      plan: planId,
      next: "/pricing"
    });
    window.location.href = `/login?${params.toString()}`;
  };

  useEffect(() => {
    let cancelled = false;

    const syncGate = async () => {
      try {
        const response = await fetch("/api/access-gate/status", { credentials: "same-origin" });
        const data = await response.json();
        if (cancelled) return;

        if (data?.expired) {
          setPreviewTimeLeft(0);
          setPreviewExpired(true);
          setPreviewActive(true);
          setBypassGate(false);
        } else if (typeof data?.secondsLeft === "number") {
          setPreviewTimeLeft(Math.max(0, data.secondsLeft));
          setPreviewExpired(false);
          setPreviewActive(true);
        }
      } catch (err) {
        console.warn("Access gate status unavailable, falling back to local preview timer.", err);
      }
    };

    syncGate();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (bypassGate || !previewActive) return;

    const timer = setInterval(() => {
      setPreviewTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setPreviewExpired(true);
          playNexoraXSound(150, 0.6, "sine");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [previewActive, bypassGate]);

  // ----------------------------------------------------
  // BACKEND DYNAMIC TAX CONFIGURATION & SYNC LOOP
  // ----------------------------------------------------
  const [backendBillingConfig, setBackendBillingConfig] = useState<any>(null);
  const [backendVatApplies, setBackendVatApplies] = useState<boolean>(false);
  const [backendVatRate, setBackendVatRate] = useState<number>(0);
  const [backendVatAmount, setBackendVatAmount] = useState<number>(0);

  // local Admin VAT setting fields synced to server DB
  const [adminVatStatus, setAdminVatStatus] = useState<string>("Registered");
  const [adminVatNumber, setAdminVatNumber] = useState<string>("GB982310622");
  const [adminVatEffectiveDate, setAdminVatEffectiveDate] = useState<string>("2026-04-01");
  const [adminVoluntaryRegistration, setAdminVoluntaryRegistration] = useState<boolean>(true);
  const [adminVatDisplayWordingState, setAdminVatDisplayWordingState] = useState<string>("VAT: 20% calculated at checkout where applicable.");
  const [adminVatInvoiceEnabled, setAdminVatInvoiceEnabled] = useState<boolean>(true);
  const [adminInternationalTaxWarningEnabled, setAdminInternationalTaxWarningEnabled] = useState<boolean>(true);
  const [adminAccountantReviewStatus, setAdminAccountantReviewStatus] = useState<string>("Reviewed");
  const [adminInternationalTaxHandling, setAdminInternationalTaxHandling] = useState<string>("Manual review");

  // Load from backend tax-status endpoint on mount and plan selections
  useEffect(() => {
    let activePriceValue = parsedPlans.find(p => p.id === selectedPlan)?.priceVal || 2499;
    if (selectedPlan === 'stage_presence') activePriceValue = 1000;
    if (selectedPlan === 'stage_enterprise') activePriceValue = 0;
    if (selectedPlan === 'tvip_lifetime' || selectedPlan === 't_vip_lifetime' || selectedPlan === 'vip_lifetime') activePriceValue = 0;

    const queryParams = new URLSearchParams({
      basePrice: String(activePriceValue),
      planId: selectedPlan,
      country: "GB", // defaults to UK direct clearance
      customerType: "business"
    });

    fetch(`/api/billing/tax-status?${queryParams.toString()}`)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setBackendBillingConfig(data.billingConfig);
          setBackendVatApplies(data.vatApplies);
          setBackendVatRate(data.vatRate);
          setBackendVatAmount(data.calculation.vatAmount);

          // Populate editable admin fields initially from standard backend response so they stay in alignment
          const config = data.billingConfig;
          if (config) {
            setAdminVatStatus(config.vatStatus || "Registered");
            setAdminVatNumber(config.vatNumber || "GB982310622");
            setAdminVatEffectiveDate(config.vatEffectiveDate || "2026-04-01");
            setAdminRollingTurnover(config.rollingTurnover || 125000);
            setAdminSingleMonthExpectedTurnover(config.expected30DayTurnover || 15000);
            setAdminVoluntaryRegistration(config.voluntaryRegistration !== false);
            setAdminVatDisplayWordingState(config.vatDisplayWording || "VAT: 20% calculated at checkout where applicable.");
            setAdminVatInvoiceEnabled(config.vatInvoiceEnabled !== false);
            setAdminInternationalTaxWarningEnabled(config.internationalTaxWarningEnabled !== false);
            setAdminAccountantReviewStatus(config.accountantReviewStatus || "Reviewed");
            setAdminInternationalTaxHandling(config.internationalTaxHandling || "Manual review");
          }
        }
      })
      .catch(err => console.error("Billing telemetry clearance rejected from server node:", err));
  }, [selectedPlan, billingCycle]);

  const handleBypassGate = (extensionPrice: number, optionName: string) => {
    playNexoraXSound(800, 0.35, "triangle");
    triggerToast(`Continue to login/sign-up to activate ${optionName}.`, "info");
    routeToLoginForPricing("pricing-access");
  };

  const handleResetPreview = () => {
    playNexoraXSound(440, 0.15);
    if (previewExpired) {
      triggerToast("Preview has expired for this IP/device. Login or sign up to continue.", "info");
      routeToLoginForPricing("preview-expired");
      return;
    }
    triggerToast("Preview timer is controlled server-side and cannot be restarted locally.", "info");
  };

  const handleChoosePlan = (planId: string, planLabel: string) => {
    setSelectedPlan(planId);
    playNexoraXSound(1000, 0.15, "triangle");

    if (!auth.currentUser) {
      triggerToast(`Login or sign up to continue with ${planLabel}.`, "info");
      routeToLoginForPricing("checkout", planId);
      return;
    }

    triggerToast(`✔ Configured checkout path to ${planLabel}!`, "success");
    const el = document.getElementById('secure-payment-portal-element');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const verifyAdminPricingAccess = async () => {
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : "";
      if (!token) {
        setIsAdminMode(false);
        triggerToast("Admin pricing tools require a verified Firebase admin login.", "error");
        return;
      }

      const response = await fetch("/api/admin/verify-clearance", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const data = await response.json();
      const verified = response.ok && (data?.role === "ADMIN" || data?.role === "SUPER_ADMIN");

      setIsAdminMode(verified);
      triggerToast(verified ? "Admin pricing tools verified by server." : "Admin clearance rejected by server.", verified ? "success" : "error");
    } catch (err) {
      setIsAdminMode(false);
      triggerToast("Admin verification failed. Please login again.", "error");
    }
  };

  // ----------------------------------------------------
  // SUBSCRIPTION & ACCESS DATA STRUCTURES
  // ----------------------------------------------------
  const plansData = [
    { id: 't1', baseId: 'bronze', tier: 'BRONZE', emoji: '🥉', iconName: 'bronze-shield', mappedMaint: 'Starter (£25/mo)', techAddon: 'Voice & Support Lite (Add-on)', title: 'Starter Presence', subtitle: 'Best for new businesses needing a clean first website', perks: ['Starter Website', 'Basic Hosting', 'Contact Form'], color: 'border-orange-500/40 from-orange-950/20 text-orange-400', hoverGlow: 'hover:shadow-[0_0_20px_rgba(249,115,22,0.15)]', logoColor: '#ea7317', hardware: false },
    { id: 't2', baseId: 'silver', tier: 'SILVER', emoji: '🥈', iconName: 'silver-star', mappedMaint: 'Premier (£50/mo)', techAddon: 'Support Basic (Add-on)', title: 'Business Foundation', subtitle: 'Best for local service businesses that need trust and enquiries', perks: ['Business Website', 'Domain & Email Setup', 'Basic SEO'], color: 'border-slate-400/40 from-slate-900/20 text-slate-300', hoverGlow: 'hover:shadow-[0_0_20px_rgba(203,213,225,0.15)]', logoColor: '#cbd5e1', hardware: false },
    { id: 't3', baseId: 'gold', tier: 'GOLD', emoji: '🥇', iconName: 'gold-crown', mappedMaint: 'Premier or Pro', techAddon: 'Voice, Support, Tracking & Dashboard (Add-on)', title: 'Growth Package', subtitle: 'Best for businesses ready to turn traffic into leads', perks: ['Growth Website', 'Social Bridge', 'Email Marketing Setup'], color: 'border-yellow-500/50 from-yellow-950/20 text-yellow-400 shadow-[0_0_20px_rgba(234,179,8,0.1)]', hoverGlow: 'hover:shadow-[0_0_30px_rgba(234,179,8,0.2)]', logoColor: '#f5b700', hardware: false },
    { id: 't4', baseId: 'diamond', tier: 'DIAMOND', emoji: '💎', iconName: 'cyan-diamond', mappedMaint: 'Pro or Custom', techAddon: 'Advanced AI & Driver Tracking (Quote)', title: 'Automation & App Ready', subtitle: 'Best for firms needing AI support and stronger systems', perks: ['AI Assistant', 'Automation', 'Analytics'], color: 'border-cyan-400/50 from-cyan-950/20 text-cyan-300', hoverGlow: 'hover:shadow-[0_0_20px_rgba(34,211,238,0.15)]', logoColor: '#22d3ee', hardware: true },
    { id: 't5', baseId: 'platinum', tier: 'PLATINUM', emoji: '💿', iconName: 'platinum-hexagon', mappedMaint: 'Pro or Custom', techAddon: 'Mobile App, Live Tracking & Dashboard (Quote)', title: 'Advanced Digital System', subtitle: 'Best for companies needing apps, hosting, and AI operations', perks: ['Mobile App Planning', 'Advanced Hosting', 'AI Automation'], color: 'border-teal-300/50 from-teal-950/20 text-teal-200', hoverGlow: 'hover:shadow-[0_0_20px_rgba(45,212,191,0.15)]', logoColor: '#cbd5e1', hardware: true },
    { id: 't6', baseId: 'black_diamond', tier: 'BLACK DIAMOND', emoji: '🖤', iconName: 'obsidian-diamond', mappedMaint: 'Custom Quote', techAddon: 'Enterprise Fleet & AI Support Auto (Quote)', title: 'Enterprise Buildout', subtitle: 'Best for larger custom systems and premium delivery', perks: ['Custom Portal', 'Priority Support', 'Enterprise Quote'], color: 'border-purple-600/50 from-purple-950/40 text-purple-400 shadow-[0_0_30px_rgba(168,85,247,0.3)] ring-1 ring-purple-500/40', hoverGlow: 'hover:shadow-[0_0_40px_rgba(168,85,247,0.4)]', logoColor: '#b5179e', hardware: true },
  ];

  const parsedPlans = plansData.map(p => {
    const annualPrice = adminPrices[p.baseId] || 499;
    const limits = adminLimits[p.baseId];
    const monthlyPrice = calculateMonthlyPlanPrice(annualPrice);
    const displayPriceCalculated = billingCycle === 'MONTHLY' ? monthlyPrice : annualPrice;
    return {
      ...p,
      priceVal: displayPriceCalculated,
      priceStr: billingCycle === 'MONTHLY' ? `£${formatGbp(displayPriceCalculated)}/mo` : `£${formatGbp(displayPriceCalculated)}/yr`,
      maxAgents: limits.maxAgents,
      maxTasks: limits.maxTasks
    };
  });

  // Declare fallback active plan mapping for custom staged or lifetime plans
  const maintenancePlans = [
    { id: 'maintenance_starter', name: 'Starter Maintenance', price: 25, desc: 'Monthly hosting checks, website updates, security basics, and email/domain support.', alternative: 'Includes hosting, domain, email, and basic maintenance support' },
    { id: 'maintenance_premium', name: 'Premier Maintenance', price: 50, desc: 'Monthly hosting checks, website updates, security basics, email/domain support, and SEO/content support.', alternative: 'Best add-on for Bronze, Silver, and Gold clients' },
    { id: 'maintenance_pro', name: 'Pro Maintenance', price: 99, desc: 'Monthly hosting checks, website updates, security basics, email/domain support, SEO/content support, and AI assistant checks.', alternative: 'Best add-on for Diamond, Platinum, and Black Diamond clients' }
  ];

  const activePlanObject = parsedPlans.find(p => p.id === selectedPlan) || 
    (selectedPlan === 'stage_presence' 
      ? { id: 'stage_presence', baseId: 'stage_presence', tier: 'PRESENCE DEPLOYMENT', emoji: '🌐', title: 'Presence Space Plan', subtitle: 'Staged Presence Setup', maxAgents: 1, maxTasks: 1000, priceStr: '£1,000 Setup', priceVal: 1000, perks: [], color: '', hoverGlow: '', logoColor: '', hardware: false }
      : selectedPlan === 'stage_enterprise'
      ? { id: 'stage_enterprise', baseId: 'stage_enterprise', tier: 'ENTERPRISE DEPLOYMENT', emoji: '🏢', title: 'Big Client / Enterprise Quote', subtitle: 'Enterprise Quote Setup', maxAgents: 120, maxTasks: 150000, priceStr: 'Quote Only', priceVal: 0, perks: [], color: '', hoverGlow: '', logoColor: '', hardware: false }
      : (selectedPlan === 'tvip_lifetime' || selectedPlan === 't_vip_lifetime' || selectedPlan === 'vip_lifetime')
      ? { id: 'tvip_lifetime', baseId: 'vip_lifetime', tier: 'VIP LIFETIME', emoji: '👑', title: 'VIP Quote Route', subtitle: 'VIP, lifetime, or major-client access by formal quote', maxAgents: 999, maxTasks: 999999, priceStr: 'Quote Only', priceVal: 0, perks: [], color: '', hoverGlow: '', logoColor: '', hardware: true }
      : parsedPlans[2]
    );

  // ----------------------------------------------------
  // DYNAMIC PRICING MATH FOR CORE PLANS & DEPLOYMENTS
  // ----------------------------------------------------
  let planName = '';
  let priceVal = 0;
  let maintenanceFeeVal = 0;
  let isDeploymentPlan = false;
  let isVipLifetime = false;
  const isStagedPlanSelected = selectedPlan === 'stage_presence' || selectedPlan === 'stage_enterprise';
  const isVipLifetimeSelected = selectedPlan === 'tvip_lifetime' || selectedPlan === 't_vip_lifetime' || selectedPlan === 'vip_lifetime';
  const canCheckout = chkPlan && chkMaint && chkMaintStop && chkVat && chkComm && chkDifferent && chkAgree && (!isStagedPlanSelected || chkStaged) && (!isVipLifetimeSelected || chkVip);
  const handlePaymentLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, channel: string) => {
    if (!auth.currentUser) {
      e.preventDefault();
      triggerToast(`Login or sign up before continuing to ${channel}.`, "info");
      routeToLoginForPricing("payment", selectedPlan);
      return;
    }

    if (!canCheckout) {
      e.preventDefault();
      triggerToast("Accept the checkout disclosures before opening payment.", "error");
    }
  };
  let displayBillingCycle = billingCycle === 'MONTHLY' ? '/mo' : '/yr';

  if (selectedPlan === 'stage_presence') {
    planName = 'Presence Deployment Plan';
    priceVal = 1000;
    isDeploymentPlan = true;
    maintenanceFeeVal = 0; // "applies only if active NexoraX-hosted services remain in use after deployment"
    displayBillingCycle = ' (Setup)';
  } else if (selectedPlan === 'stage_enterprise') {
    planName = 'Big Client / Enterprise Quote';
    priceVal = 10000;
    isDeploymentPlan = true;
    maintenanceFeeVal = 0; // "applies only if active NexoraX-hosted services remain in use after deployment"
    displayBillingCycle = ' (Setup)';
  } else if (selectedPlan === 'tvip_lifetime' || selectedPlan === 't_vip_lifetime' || selectedPlan === 'vip_lifetime') {
    planName = 'VIP Lifetime Access';
    priceVal = 150000;
    isVipLifetime = true;
    displayBillingCycle = ' (Custom)';
    
    if (vipMaintOption === 'INCLUDED') {
      maintenanceFeeVal = 0;
    } else if (vipMaintOption === 'ANNUAL_10') {
      maintenanceFeeVal = 15000; // 10% annual
    } else if (vipMaintOption === 'MONTHLY_10_ACTIVE') {
      maintenanceFeeVal = 15000; // 10% monthly
    } else {
      maintenanceFeeVal = 0; // Custom enterprise agreement (default)
    }
  } else {
    const planObj = parsedPlans.find(p => p.id === selectedPlan) || parsedPlans[2];
    planName = planObj.tier + ' Plan';
    const baseAdminPrice = adminPrices[planObj.baseId] || 2499;
    const isYearly = billingCycle === 'YEARLY';
    priceVal = isYearly ? Math.floor(baseAdminPrice * 0.90) : baseAdminPrice;
    
    // Maintenance fee is 10% of display price while active
    maintenanceFeeVal = priceVal * 0.10;
  }

  // Subtotal before VAT (calculated as base price + maintenance fee)
  const subtotalBeforeVAT = priceVal + (isDeploymentPlan ? 0 : maintenanceFeeVal);
  const vatApplies = backendBillingConfig 
    ? (backendBillingConfig.vatStatus === "Registered" || backendVatApplies)
    : (adminRollingTurnover >= 90000 || adminSingleMonthExpectedTurnover >= 90000);
  const currentVatRate = vatApplies ? 0.20 : 0.0;
  const vatCalculated = backendBillingConfig ? backendVatAmount : (subtotalBeforeVAT * currentVatRate);
  const grandTotalDue = subtotalBeforeVAT + vatCalculated;

  const backendVatNumberText = backendBillingConfig ? backendBillingConfig.vatNumber : adminVatNumber;
  const backendInternationalTaxWarningEnabled = backendBillingConfig 
    ? backendBillingConfig.internationalTaxWarningEnabled 
    : adminInternationalTaxWarningEnabled;
  const backendInternationalTaxHandling = backendBillingConfig 
    ? backendBillingConfig.internationalTaxHandling 
    : adminInternationalTaxHandling;

  const isCommunityAllocationComplete = 
    !!(adminBeneficiary1?.trim()) && 
    adminPercent1 > 0 &&
    !!(adminBeneficiary2?.trim()) && 
    adminPercent2 > 0 &&
    !!(adminBeneficiary3?.trim()) && 
    adminPercent3 > 0 &&
    !!(adminCalcBasis?.trim()) &&
    !!(adminExcludeVAT?.trim()) &&
    !!(adminExcludeRefunds?.trim()) &&
    !!(adminTimePeriod?.trim()) &&
    !!(adminCap?.trim()) &&
    adminPublicDisclosure;

  const priceInGbp = priceVal;
  const operationalAllocationVal = priceVal * 0.20;

  // Next Renewal Date calculation
  const getRenewalDateStr = () => {
    if (isVipLifetime) return "Enterprise Maintenance Schedule";
    if (isDeploymentPlan) return "Staged Milestones Delivery";
    const d = new Date();
    if (billingCycle === 'MONTHLY') {
      d.setMonth(d.getMonth() + 1);
    } else {
      d.setFullYear(d.getFullYear() + 1);
    }
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // ----------------------------------------------------
  // ADMIN-ONLY ACCOUNTING MATHEMATICS
  // ----------------------------------------------------
  const adminGrossRevenue = priceVal;
  const adminVat = subtotalBeforeVAT * currentVatRate;
  const adminProviderFee = priceVal * 0.015; // Simulated 1.5% fee
  
  // 3% Community Allocation split equally (1% each)
  const adminTotalCommunityAllocation = priceVal * 0.03;
  const adminCharity1Allocation = priceVal * 0.01;
  const adminCharity2Allocation = priceVal * 0.01;
  const adminCharity3Allocation = priceVal * 0.01;
  const adminMaintenanceFee = isDeploymentPlan ? 0 : maintenanceFeeVal;
  
  // Net revenue = base price - community allocation - provider fee
  const adminNetRevenue = priceVal - adminTotalCommunityAllocation - adminProviderFee;
  const adminOperationalReserve = adminNetRevenue * 0.40; // 40% target for operational reserve

  // Old public allocations mapped cleanly to prevent runtime lookup errors
  const communityAllocation = priceVal * 0.03;
  const charitySplit = priceVal * 0.01;

  // Filtered pricing tiers based on user search
  const filteredPlans = parsedPlans.filter(p => 
    p.tier.toLowerCase().includes(tiersSearch.toLowerCase()) ||
    p.title.toLowerCase().includes(tiersSearch.toLowerCase()) ||
    p.subtitle.toLowerCase().includes(tiersSearch.toLowerCase()) ||
    p.perks.some(perk => perk.toLowerCase().includes(tiersSearch.toLowerCase()))
  );

  // Wording validation & secure invoice action
  const handleRequestInvoice = (e: React.FormEvent) => {
    e.preventDefault();

    if (!auth.currentUser) {
      triggerToast("Login or sign up before requesting a secure invoice.", "info");
      routeToLoginForPricing("invoice", selectedPlan);
      return;
    }
    
    const isStagedPlanSelected = selectedPlan === 'stage_presence' || selectedPlan === 'stage_enterprise';
    const isVipLifetimeSelected = selectedPlan === 'tvip_lifetime' || selectedPlan === 't_vip_lifetime' || selectedPlan === 'vip_lifetime';
    
    const isMainTermsValid = chkPlan && chkMaint && chkMaintStop && chkVat && chkComm && chkDifferent && chkAgree;
    const isStagedTermsValid = !isStagedPlanSelected || chkStaged;
    const isVipTermsValid = !isVipLifetimeSelected || chkVip;
    const isInvoiceTermsValid = chkInvoiceA && chkInvoiceB;

    if (!isMainTermsValid || !isStagedTermsValid || !isVipTermsValid || !isInvoiceTermsValid) {
      triggerToast("⚠ Required: Please review and accept all required checkboxes and invoice terms below.", "warning");
      return;
    }

    playNexoraXSound(1000, 0.45, "triangle");

    // Add simulated invoice log
    const newInvId = `NX-2026-${Math.floor(100 + Math.random() * 900)}`;
    const newInv = {
      id: newInvId,
      date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      amount: `£${grandTotalDue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      status: 'PENDING' as const
    };
    setSimulatedInvoices([newInv, ...simulatedInvoices]);
    
    // Auto sync registry
    triggerToast(`✔ INVOICE REGISTERED: ${newInvId} uploaded to NexoraX Resource Library via CloudSync-02!`, "success");

    // Clear form
    setInvoiceCompanyName('');
    setInvoiceEmail('');
    setInvoiceAddress('');
  };

  const handleSubmitDriverQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!driverQuoteName || !driverQuoteEmail) {
      triggerToast("Name and Email parameters are mandatory to initialize inquiry.", "error");
      return;
    }

    setDriverQuoteSubmitting(true);
    setDriverQuoteError(null);
    setDriverQuoteSuccess(false);

    try {
      const fullMessage = `[Enquiry Type: Driver / Courier / Fleet Systems]
[Business Type: ${driverQuoteBusinessType}]
[Vehicles: ${driverQuoteVehicles}]
[Required Features: ${driverQuoteFeatures.join(', ')}]

${driverQuoteMessage}`;

      const payload = {
        name: driverQuoteName,
        businessName: driverQuoteBusiness,
        email: driverQuoteEmail,
        phone: driverQuotePhone,
        interestedService: "Driver / Courier / Fleet Systems",
        sourceTag: "Driver / Courier / Fleet Systems Form",
        message: fullMessage
      };

      const response = await fetch('/api/launch-list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Server returned HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setDriverQuoteSuccess(true);
        playNexoraXSound(880, 0.35, "sine");
        triggerToast("✔ Custom quote request successfully registered.", "success");
        // Clear form fields
        setDriverQuoteName('');
        setDriverQuoteBusiness('');
        setDriverQuoteEmail('');
        setDriverQuotePhone('');
        setDriverQuoteBusinessType('Courier');
        setDriverQuoteVehicles('');
        setDriverQuoteFeatures([]);
        setDriverQuoteMessage('');
      } else {
        throw new Error(result.error || "Server registration failed.");
      }
    } catch (err: any) {
      console.error("Quote submission error:", err);
      setDriverQuoteError("We could not send your quote request at this moment. Please try again or contact NexoraX Digital directly.");
      triggerToast("⚠ Submission failed. Please review error guidelines.", "error");
    } finally {
      setDriverQuoteSubmitting(false);
    }
  };

  // Outreach Template Handler
  const handleCopyScript = () => {
    playNexoraXSound(700, 0.2);
    navigator.clipboard.writeText(getOutreachScript());
    triggerToast("📋 NexoraX Outreach Script copied to clipboard successfully!", "success");
  };

  const getOutreachScript = () => {
    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    return `**STATUS:** READY FOR TRANSMISSION
**FROM:** Office of the Director, NexoraX Digital Ltd (SC887095)
**DATE:** ${today}

---

### BUSINESS PIPELINE TARGET: ${targetAgency || '[INSERT AGENCY]'} (${candidateName || '[INSERT CANDIDATE_NAME]'})
**TO:** ${outreachEmail || 'acquisitions@agency.co.uk'}
**SUBJECT:** SECURE CORPORATE PARTNERSHIP: Specialised Private Secretariat Onboarding | VIP Access Protocol

To the Executive Support Division, ${targetAgency || 'Agency'}:

This correspondence serves as a formal invitation from NexoraX Digital Ltd (SC887095). Under our strategic business development guidelines, we are coordinating the fast-track integration of our specialised Private Secretariat support role to anchor our core digital infrastructures.

The position functions as a structural catalyst for the stabilization of our proprietary AI infrastructure and the active coordination of our commercial workflows. Standard operations must execute with zero friction and strict discretion within our secure corporate Google Workspace environment (@nexorax.co.uk).

**FISCAL FRAMEWORK (COMMERCIAL-READINESS COMPLETED):**
This onboarding executes under a secure Commercial-Readiness framework, pending formal accountant and legal audit. Remuneration and fees are fully secured and mapped under a structured Billing Agreement with direct corporate alignment. 

Our standard secure client-support node terms apply, which include a standard 10% Maintenance & Hosting Fee for ongoing platform, hosting, and managed AI workflows while active hosted services remain in use. The fee stops automatically when active service is cancelled or no longer provided.

We require a partner alignment sequence for ${candidateName || 'Candidate Name'} to match these secure requirements, bypassing administrative bottlenecks.

**ACTION REQUIRED:**
Coordinate with the Office of the Director immediately at **support@nexoraxdigital.co.uk** or call our verified line to align onboarding workflows.

**Office of the Director**
NexoraX Digital Ltd (SC887095)`;
  };

  return (
    <div className="relative w-full space-y-16 pb-24 text-slate-100 select-none">
      
      {/* ----------------------------------------------------
          SECTION 1 — 60-SECOND PREVIEW ACCESS / GATE
          ---------------------------------------------------- */}
      {previewActive && !bypassGate && (
        <div className="relative w-full max-w-5xl mx-auto px-4 z-40">
          <div className="bg-[#050914]/90 border-[2px] border-cyan-500/30 rounded-3xl p-6 shadow-[0_0_35px_rgba(6,182,212,0.15)] backdrop-blur-xl flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl pointer-events-none" />
            
            <div className="flex items-center gap-4 text-left">
              <div className="p-3.5 bg-cyan-500/10 border border-cyan-400/30 rounded-2xl shrink-0 animate-pulse">
                <Clock className="w-7 h-7 text-cyan-400" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded bg-emerald-500 animate-ping"></span>
                  <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-cyan-400 font-extrabold">Active Sandbox Telemetry</span>
                </div>
                <h4 className="text-xl font-sans font-black text-white uppercase tracking-tight">60-Second Live Sandbox Mode</h4>
                <p className="text-[10.5px] font-mono text-slate-400 max-w-xl normal-case">
                  METADATA SYNC: OK // SecurePayment BALANCES: VERIFIED // GATE SECURED: LOCAL EXPOSURE. You have temporarily unlocked full navigation views.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 shrink-0 bg-black/60 border border-slate-900 rounded-2xl p-4 min-w-[200px] justify-center">
              <div className="text-center">
                <div className="text-3xl font-mono font-black text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]">
                  00:{String(previewTimeLeft).padStart(2, '0')}
                </div>
                <span className="text-[8px] font-mono text-slate-500 uppercase font-black">SEC INDEX LEFT</span>
              </div>
              <div className="w-px h-8 bg-slate-800" />
              <button 
                onClick={handleResetPreview}
                className="p-2.5 bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-cyan-500/40 rounded-xl text-slate-400 hover:text-white transition-all cursor-pointer"
                title="Restart Telemetry Window"
              >
                <RefreshCw size={14} className="animate-spin-slow" />
              </button>
            </div>
          </div>

          {/* PREVIEW EXPIRED OVERLAY GLASS MODAL */}
          <AnimatePresence>
            {false && previewExpired && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[500] bg-black/95 backdrop-blur-3xl flex items-center justify-center p-4"
              >
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="max-w-2xl w-full text-center space-y-8 bg-[#030611] border-2 border-fuchsia-500/50 rounded-3xl p-10 shadow-[0_0_60px_rgba(232,64,243,0.3)] relative overflow-hidden"
                >
                  <div className="absolute top-0 left-1/4 w-96 h-96 bg-fuchsia-500/5 rounded-full blur-[100px] pointer-events-none" />
                  
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-fuchsia-950/40 border border-fuchsia-500/40 rounded-2xl mx-auto flex items-center justify-center text-3xl shadow-[0_0_20px_rgba(232,64,243,0.2)]">
                      🔒
                    </div>
                    <div className="inline-block px-3.5 py-1 bg-fuchsia-900/40 border border-fuchsia-500/40 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] text-fuchsia-300 font-extrabold">
                      PREVIEW TIMEOUT Revoked
                    </div>
                    <h3 className="text-3xl md:text-4xl font-sans font-black text-white uppercase tracking-tight">60-Second Local Sandbox Paused</h3>
                    <p className="text-xs font-mono text-slate-400 uppercase tracking-widest max-w-xl mx-auto leading-relaxed">
                      To safeguard background daemon tasks, query meters, and secure access channels, please configure your pricing trajectory or choose an access option to continue.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button 
                      onClick={() => routeToLoginForPricing("access-extension")}
                      className="py-3.5 px-6 border border-[#00ffff] hover:bg-[#00ffff]/10 bg-cyan-950/20 text-[#00ffff] hover:text-white rounded-xl font-mono text-xs font-black uppercase tracking-widest shadow-[0_0_20px_rgba(0,255,255,0.2)] transition-all transform hover:scale-[1.02] cursor-pointer text-center"
                    >
                      Login / Sign Up To Continue
                    </button>
                    <button 
                      onClick={() => routeToLoginForPricing("full-details")}
                      className="py-3.5 px-6 bg-gradient-to-r from-cyan-400 to-[#e840f3]/80 text-black rounded-xl font-mono text-xs font-black uppercase tracking-widest hover:shadow-[0_0_20px_rgba(232,64,243,0.4)] transition-all transform hover:scale-[1.02] cursor-pointer text-center"
                    >
                      Full Details Page
                    </button>
                  </div>

                  <div className="pt-4 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-slate-400 text-[10.5px] font-mono">
                    <span className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      Freephone Desk: <strong className="text-white">08000 622827</strong>
                    </span>
                    <button 
                      onClick={handleResetPreview}
                      className="text-cyan-400 hover:text-cyan-300 font-extrabold hover:underline"
                    >
                      RESET LOCAL SANDBOX PREVIEW
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* ----------------------------------------------------
          PUBLIC PRICING HERO SECTION
          ---------------------------------------------------- */}
      <div className="max-w-7xl mx-auto w-full px-6 text-center space-y-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-900/40 border border-purple-500/40 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300 font-extrabold">
          <Award className="w-3.5 h-3.5 text-purple-400 animate-spin-slow" />
          NexoraX Pricing Engine Active
        </div>
        
        <h2 className="text-4xl md:text-6xl lg:text-[5.5rem] font-sans font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-purple-400 leading-none uppercase tracking-tight max-w-5xl mx-auto">
          Deploy Ready-Made <span className="text-cyan-400">AI Workers</span> for Your Business
        </h2>
        
        <p className="text-sm md:text-lg text-slate-300 font-sans max-w-3xl mx-auto leading-relaxed font-bold">
          Choose from monthly growth plans or custom enterprise packages. Automate support, sales, admin, and social media campaigns under secure payment agreements.
        </p>

        {/* CONTROLS: CYCLES & QUICK SEARCHES */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 pt-4 z-20">
          
          {/* Monthly/Yearly switch toggle */}
          <div className="bg-[#0b0f19] border border-cyan-500/30 p-1 flex rounded-full shadow-[0_0_20px_rgba(0,255,255,0.15)] relative backdrop-blur-sm shrink-0">
            <button
              onClick={() => {
                setBillingCycle('MONTHLY');
                playNexoraXSound(500, 0.15);
              }}
              className={`px-6 py-2.5 rounded-full font-mono text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all cursor-pointer ${billingCycle === 'MONTHLY' ? 'bg-[#00ffff] text-black shadow-[0_0_12px_cyan]' : 'text-slate-400 hover:text-white'}`}
            >
              Pay Monthly
            </button>
            <button
              onClick={() => {
                setBillingCycle('YEARLY');
                playNexoraXSound(600, 0.2);
              }}
              className={`px-6 py-2.5 rounded-full font-mono text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 cursor-pointer ${billingCycle === 'YEARLY' ? 'bg-[#00ffff] text-black shadow-[0_0_12px_cyan]' : 'text-slate-400 hover:text-white'}`}
            >
              Pay Yearly <span className={`text-[8px] px-1.5 py-0.5 rounded font-black ${billingCycle === 'YEARLY' ? 'bg-black text-cyan-300' : 'bg-cyan-500/10 text-cyan-400'}`}>ONE-OFF</span>
            </button>
          </div>

          <div className="relative w-full max-w-xs shrink-0 self-stretch md:self-auto">
            <input 
              type="text"
              value={tiersSearch}
              onChange={(e) => setTiersSearch(e.target.value)}
              placeholder="Search features..."
              className="w-full bg-black/60 border border-slate-800 rounded-full h-11 px-5 font-mono text-[11px] text-white placeholder-slate-500 focus:border-cyan-400 outline-none"
            />
            {tiersSearch && (
              <button 
                onClick={() => setTiersSearch('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white font-bold"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------
          CUSTOMER BUYING GUIDE — "WHAT SHOULD I BUY FIRST?"
          ---------------------------------------------------- */}
      <div className="max-w-7xl mx-auto px-6 w-full space-y-8 z-10 scroll-mt-24" id="buying-guide-section">
        <div className="flex flex-col items-center text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-950/60 border border-cyan-500/40 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300 font-extrabold animate-pulse">
            🧭 Buying Compass
          </div>
          <h3 className="text-3xl md:text-5xl font-sans font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-purple-400 leading-none uppercase tracking-tight">
            What Should I Buy First?
          </h3>
          <p className="text-xs md:text-sm font-sans text-slate-400 max-w-xl mx-auto leading-relaxed">
            Follow the premium route guide below to identify the ideal entry point for your business velocity.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {/* Card 1: Need quick access? */}
          <div className="glass-panel border-2 border-cyan-500/20 hover:border-cyan-400 bg-black/45 rounded-[2rem] p-6 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(0,255,255,0.15)] transition-all duration-300 relative group">
            <div className="space-y-4">
              <span className="text-2xl">⚡</span>
              <h4 className="text-lg font-sans font-black text-white uppercase tracking-wider">Need quick access?</h4>
              <div className="text-[9.5px] font-mono text-cyan-400 uppercase tracking-widest font-black">
                Route: 7-Day Sprint, 15-Day Expansion, 30-Day Mastery
              </div>
              <p className="text-[11.5px] font-sans text-slate-300 leading-relaxed normal-case">
                Unlock temporary access, review the workspace, and start your planning window.
              </p>
            </div>
            <button
              onClick={() => document.getElementById('access-extensions-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="mt-6 w-full py-2.5 bg-cyan-950/60 border border-cyan-500/40 hover:bg-cyan-400 hover:text-black hover:shadow-[0_0_12px_rgba(0,255,255,0.3)] text-cyan-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-xl cursor-pointer transition-all"
            >
              View Access Passes
            </button>
          </div>

          {/* Card 2: Need a website package? */}
          <div className="glass-panel border-2 border-purple-500/20 hover:border-[#e840f3] bg-black/45 rounded-[2rem] p-6 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(232,64,243,0.15)] transition-all duration-300 relative group">
            <div className="space-y-4">
              <span className="text-2xl">🌐</span>
              <h4 className="text-lg font-sans font-black text-white uppercase tracking-wider">Need a website package?</h4>
              <div className="text-[9.5px] font-mono text-purple-400 uppercase tracking-widest font-black">
                Route: Bronze, Silver, Gold
              </div>
              <p className="text-[11.5px] font-sans text-slate-300 leading-relaxed normal-case">
                Choose a ready business package, start with Bronze/Silver, or secure your Gold build with a deposit.
              </p>
            </div>
            <button
              onClick={() => document.getElementById('membership-plans-view')?.scrollIntoView({ behavior: 'smooth' })}
              className="mt-6 w-full py-2.5 bg-purple-950/60 border border-[#e840f3]/40 hover:bg-[#e840f3] hover:text-white hover:shadow-[0_0_12px_rgba(232,64,243,0.3)] text-purple-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-xl cursor-pointer transition-all"
            >
              View Website Packages
            </button>
          </div>

          {/* Card 3: Already have a website? */}
          <div className="glass-panel border-2 border-emerald-500/20 hover:border-emerald-400 bg-black/45 rounded-[2rem] p-6 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(16,185,129,0.15)] transition-all duration-300 relative group">
            <div className="space-y-4">
              <span className="text-2xl">🔧</span>
              <h4 className="text-lg font-sans font-black text-white uppercase tracking-wider">Already have a website?</h4>
              <div className="text-[9.5px] font-mono text-emerald-400 uppercase tracking-widest font-black">
                Route: Starter, Premier, Pro Maintenance
              </div>
              <p className="text-[11.5px] font-sans text-slate-300 leading-relaxed normal-case">
                Keep your website, hosting, email, automation, and support running smoothly.
              </p>
            </div>
            <button
              onClick={() => document.getElementById('maintenance-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="mt-6 w-full py-2.5 bg-emerald-950/60 border border-emerald-500/40 hover:bg-emerald-400 hover:text-black hover:shadow-[0_0_12px_rgba(16,185,129,0.3)] text-emerald-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-xl cursor-pointer transition-all"
            >
              View Maintenance Plans
            </button>
          </div>

          {/* Card 4: Want ready-made products? */}
          <div className="glass-panel border-2 border-amber-500/20 hover:border-amber-400 bg-black/45 rounded-[2rem] p-6 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(245,158,11,0.15)] transition-all duration-300 relative group">
            <div className="space-y-4">
              <span className="text-2xl">📦</span>
              <h4 className="text-lg font-sans font-black text-white uppercase tracking-wider">Want ready-made products?</h4>
              <div className="text-[9.5px] font-mono text-amber-400 uppercase tracking-widest font-black">
                Route: Digital Products tab
              </div>
              <p className="text-[11.5px] font-sans text-slate-300 leading-relaxed normal-case">
                Buy pre-built websites, mobile app templates, dashboards, PDFs, pitch decks, social media packs, AI assistant templates, and automation kits.
              </p>
            </div>
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('nexorax-trigger-tab-change', { detail: 'Digital Products' }));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="mt-6 w-full py-2.5 bg-amber-950/60 border border-amber-500/40 hover:bg-amber-500 hover:text-black hover:shadow-[0_0_12px_rgba(245,158,11,0.3)] text-amber-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-xl cursor-pointer transition-all"
            >
              View Digital Products
            </button>
          </div>

          {/* Card 5: Need apps, dashboards, AI, or live tracking? */}
          <div className="glass-panel border-2 border-blue-500/20 hover:border-blue-400 bg-black/45 rounded-[2rem] p-6 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(59,130,246,0.15)] transition-all duration-300 relative group">
            <div className="space-y-4">
              <span className="text-2xl">🤖</span>
              <h4 className="text-lg font-sans font-black text-white uppercase tracking-wider">Need apps, dashboards, or AI?</h4>
              <div className="text-[9.5px] font-mono text-blue-400 uppercase tracking-widest font-black">
                Route: Diamond, Platinum, Black Diamond, VIP
              </div>
              <p className="text-[11.5px] font-sans text-slate-300 leading-relaxed normal-case">
                Request a custom buildout for apps, dashboards, AI assistants, driver tracking, admin portals, and advanced business systems.
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedPlan('t4');
                document.getElementById('secure-payment-portal-element')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="mt-6 w-full py-2.5 bg-blue-950/60 border border-blue-500/40 hover:bg-blue-400 hover:text-black hover:shadow-[0_0_12px_rgba(59,130,246,0.3)] text-blue-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-xl cursor-pointer transition-all"
            >
              Request Custom Quote
            </button>
          </div>

          {/* Card 6: Need driver tracking or courier systems? */}
          <div className="glass-panel border-2 border-orange-500/20 hover:border-orange-400 bg-black/45 rounded-[2rem] p-6 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(249,115,22,0.15)] transition-all duration-300 relative group">
            <div className="space-y-4">
              <span className="text-2xl">🚚</span>
              <h4 className="text-lg font-sans font-black text-white uppercase tracking-wider">Need driver tracking or courier systems?</h4>
              <div className="text-[9.5px] font-mono text-orange-400 uppercase tracking-widest font-black">
                Route: Custom proposal
              </div>
              <p className="text-[11.5px] font-sans text-slate-300 leading-relaxed normal-case">
                Build a courier, delivery, taxi, field-service, or logistics system with website, driver dashboard, dispatch panel, customer tracking page, and optional live GPS tracking.
              </p>
            </div>
            <button
              onClick={() => {
                document.getElementById('driver-fleet-quote')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="mt-6 w-full py-2.5 bg-orange-950/60 border border-orange-500/40 hover:bg-orange-400 hover:text-black hover:shadow-[0_0_12px_rgba(249,115,22,0.3)] text-orange-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-xl cursor-pointer transition-all"
            >
              View Driver Systems
            </button>
          </div>

          {/* Card 7: Not sure yet? */}
          <div className="glass-panel border-2 border-pink-500/20 hover:border-pink-400 bg-black/45 rounded-[2rem] p-6 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(244,63,94,0.15)] transition-all duration-300 relative group">
            <div className="space-y-4">
              <span className="text-2xl">🔮</span>
              <h4 className="text-lg font-sans font-black text-white uppercase tracking-wider">Not sure yet?</h4>
              <div className="text-[9.5px] font-mono text-pink-400 uppercase tracking-widest font-black">
                Route: Ask NexoraX
              </div>
              <p className="text-[11.5px] font-sans text-slate-300 leading-relaxed normal-case">
                Tell us your goal and NexoraX will recommend the best package, product, or custom quote route.
              </p>
            </div>
            <button
              onClick={() => {
                alert("NOT SURE YET?\n\nCall our free advisory hotline at 08000 622827 to speak directly with our lead planning engineer, or fill in the Custom Proposal request below.");
                document.getElementById('secure-payment-portal-element')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="mt-6 w-full py-2.5 bg-pink-950/60 border border-pink-500/40 hover:bg-pink-400 hover:text-black hover:shadow-[0_0_12px_rgba(244,63,94,0.3)] text-pink-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-xl cursor-pointer transition-all"
            >
              Ask NexoraX
            </button>
          </div>
        </div>

        {/* Buying Guide Note */}
        <div className="text-center pt-2">
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-wide">
            💡 “Free calculators and planning tools are available to help estimate website, maintenance, automation, and launch costs before buying.”
          </p>
        </div>
      </div>

      {/* ----------------------------------------------------
          SECTION 2 — ACCESS EXTENSIONS
          ---------------------------------------------------- */}
      <div className="max-w-7xl mx-auto px-6 w-full space-y-6 scroll-mt-24" id="access-extensions-section">
        <div className="flex flex-col items-center text-center space-y-1">
          <h3 className="text-sm font-black font-mono text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
            <Layers className="w-4 h-4 text-cyan-400" />
            NexoraX Access Extensions
          </h3>
          <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
            Clearance options bypass funnel restrictions securely
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
              { id: 'ext2', title: '7-DAY SPRINT', subtitle: 'Rapid Ignition Plan', basePrice: 199, desc: 'One-week access for customers who need a focused sprint, onboarding, and project review window.', benefit: 'Fast Onboarding Included', icon: '⚡', color: 'border-cyan-500/20 hover:border-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.1)]' },
              { id: 'ext3', title: '15-DAY EXPANSION', subtitle: 'Velocity Scaling Deck', basePrice: 299, desc: 'Two-week access for deeper workspace review, project planning, and digital operations setup.', benefit: 'Dedicated Project Manager', icon: '🚀', color: 'border-indigo-500/20 hover:border-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.1)]' },
              { id: 'ext4', title: '30-DAY MASTERY', subtitle: 'Apex Growth Window', basePrice: 699, desc: 'Full month access for serious customers who need strategy, build planning, and launch preparation.', benefit: 'Gold Core Included', icon: '🔮', color: 'border-purple-600/30 hover:border-purple-400 shadow-[0_0_20px_rgba(168,85,247,0.15)]' }
            ].map((ext) => (
            <div 
              key={ext.id}
              className={`bg-black/90 border-2 rounded-2xl p-5 flex flex-col justify-between text-left transition-all hover:translate-y-[-2px] group ${ext.color}`}
            >
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-3xl filter drop-shadow-sm group-hover:scale-110 transition-transform">{ext.icon}</span>
                  <div className="text-right">
                    <span className="text-lg font-mono font-black text-white">£{ext.basePrice.toLocaleString()}</span>
                    <span className="text-[8.5px] font-mono text-slate-500 block">ONE-TIME FEE</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider">{ext.title}</h4>
                  <p className="text-[9px] font-mono text-slate-500 uppercase tracking-widest">{ext.subtitle}</p>
                  <p className="text-[10px] text-slate-400 leading-relaxed font-sans pt-1.5 normal-case">{ext.desc}</p>
                </div>
              </div>

              <div className="mt-5 space-y-2 border-t border-slate-900 pt-3">
                <div className="bg-slate-900/60 p-2 rounded border border-slate-800 text-[8.5px] font-mono text-cyan-300 font-bold uppercase tracking-wider text-center">
                  {ext.benefit}
                </div>
                {(() => {
                  let buttonLabel = `Clearance ${ext.title === 'VIP Access Protocol' ? 'VIP' : 'Initiate'}`;
                  let paymentLink: string | undefined = undefined;
                  if (ext.id === 'ext2') {
                    buttonLabel = 'Choose 7-Day Sprint';
                    paymentLink = TIDE_PAYMENT_LINK_7_DAY_SPRINT;
                  } else if (ext.id === 'ext3') {
                    buttonLabel = 'Choose 15-Day Expansion';
                    paymentLink = TIDE_PAYMENT_LINK_15_DAY_EXPANSION;
                  } else if (ext.id === 'ext4') {
                    buttonLabel = 'Choose 30-Day Mastery';
                    paymentLink = TIDE_PAYMENT_LINK_30_DAY_MASTERY;
                  }

                  const handleButtonClick = () => {
                    if (paymentLink) {
                      const isRealLink = paymentLink.startsWith('http://') || paymentLink.startsWith('https://');
                      if (isRealLink) {
                        window.open(paymentLink, '_blank', 'noopener,noreferrer');
                      } else {
                        playNexoraXSound(1200, 0.2);
                        triggerToast("Payment link is being prepared. Please contact NexoraX Digital for activation.", "info");
                      }
                    } else {
                      handleBypassGate(ext.basePrice, ext.title);
                    }
                  };

                  let qrCodeImage = '';
                  if (ext.id === 'ext2') qrCodeImage = '/payments/tide-qr-7-day-sprint.png';
                  else if (ext.id === 'ext3') qrCodeImage = '/payments/tide-qr-15-day-expansion.png';
                  else if (ext.id === 'ext4') qrCodeImage = '/payments/tide-qr-30-day-mastery.png';

                  return (
                    <div className="space-y-2 w-full">
                      <button
                        onClick={handleButtonClick}
                        className="w-full py-2 bg-gradient-to-r from-cyan-400 to-[#e840f3] hover:shadow-[0_0_12px_rgba(0,255,255,0.3)] text-black font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center"
                      >
                        {buttonLabel}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedQrInfo({
                            title: ext.title,
                            price: ext.basePrice,
                            image: qrCodeImage,
                            url: paymentLink
                          });
                        }}
                        className="w-full py-1.5 bg-slate-950/60 hover:bg-slate-900 border border-slate-800 text-slate-300 hover:text-white font-mono text-[9px] uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center"
                      >
                        Scan QR
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>

        {/* Custom Proposal Payment & Surcharge Directives */}
        <div className="mt-6 bg-[#070c1a]/95 border border-cyan-500/20 rounded-2xl p-6 text-left max-w-7xl mx-auto shadow-[0_0_20px_rgba(0,255,255,0.05)]">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
            <div className="space-y-3 w-full">
              <h4 className="text-xs font-mono font-black text-cyan-400 uppercase tracking-widest">
                NexoraX Payment &amp; Custom Proposal Directives
              </h4>
              <ul className="list-disc pl-4 space-y-1.5 font-mono text-[10px] uppercase text-slate-300 tracking-wider">
                <li>No payment is required until a preview/final proposal is accepted for custom work.</li>
                <li>Split-payment projects require a 30% secured deposit to initiate deployment.</li>
                <li>Monthly and bi-monthly split payments include a 20% processing/service surcharge.</li>
                <li>Quarterly split payments include a 30% processing/service surcharge.</li>
                <li>Ongoing maintenance and hosting plans do not include the 20% or 30% surcharge.</li>
              </ul>

              <div className="border-t border-slate-800 pt-3 mt-2">
                <h5 className="text-[10px] font-mono font-black text-cyan-400 uppercase tracking-widest mb-1.5">
                  Access Tier Payment Links
                </h5>
                <p className="text-[10px] font-mono uppercase text-slate-400 tracking-wider leading-relaxed">
                  The 7-Day Sprint, 15-Day Expansion, and 30-Day Mastery access tiers may be purchased through secure payment links provided by NexoraX Digital. Once payment is confirmed, the customer’s access period will be activated according to the selected tier.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------
          SECTION 3 — MONTHLY GROWTH PLANS (6 CARDS)
          ---------------------------------------------------- */}
      <div className="max-w-7xl mx-auto px-6 w-full space-y-8 scroll-mt-24" id="membership-plans-view">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="space-y-1">
            <h3 className="text-sm font-black font-mono text-cyan-400 uppercase tracking-widest flex items-center justify-center gap-1.5">
              <Database className="w-4 h-4 text-cyan-400" />
              NexoraX Growth Plans
            </h3>
            <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
              Toggle yearly one-off pricing or monthly payments with 20% uplift
            </p>
          </div>

          <div className="bg-[#0b0f19] border border-cyan-500/30 p-1 flex rounded-full shadow-[0_0_20px_rgba(0,255,255,0.15)] relative backdrop-blur-sm">
            <button
              onClick={() => {
                setBillingCycle('YEARLY');
                playNexoraXSound(600, 0.2);
              }}
              className={`px-6 py-2.5 rounded-full font-mono text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all cursor-pointer ${billingCycle === 'YEARLY' ? 'bg-[#00ffff] text-black shadow-[0_0_12px_cyan]' : 'text-slate-400 hover:text-white'}`}
            >
              Pay Yearly <span className={`text-[8px] px-1.5 py-0.5 rounded font-black ${billingCycle === 'YEARLY' ? 'bg-black text-cyan-300' : 'bg-cyan-500/10 text-cyan-400'}`}>ONE-OFF</span>
            </button>
            <button
              onClick={() => {
                setBillingCycle('MONTHLY');
                playNexoraXSound(500, 0.15);
              }}
              className={`px-6 py-2.5 rounded-full font-mono text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all cursor-pointer ${billingCycle === 'MONTHLY' ? 'bg-[#00ffff] text-black shadow-[0_0_12px_cyan]' : 'text-slate-400 hover:text-white'}`}
            >
              Pay Monthly <span className={`text-[8px] px-1.5 py-0.5 rounded font-black ${billingCycle === 'MONTHLY' ? 'bg-black text-cyan-300' : 'bg-cyan-500/10 text-cyan-400'}`}>+20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredPlans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            return (
              <div 
                key={plan.id}
                onClick={() => {
                  setSelectedPlan(plan.id);
                  playNexoraXSound(550, 0.1);
                  triggerToast(`🎯 Active selection changed to ${plan.tier}! Scroll to summary/checkout.`, "info");
                }}
                className={`bg-[#050914]/95 border-2 rounded-[2rem] p-6 text-left flex flex-col justify-between transition-all duration-300 relative overflow-hidden group cursor-pointer ${
                  isSelected 
                    ? "border-cyan-400 bg-[#0d011c]/90 shadow-[0_0_25px_rgba(0,255,255,0.25)] scale-[1.02]" 
                    : "border-slate-800 hover:border-cyan-500/30"
                } ${plan.hoverGlow}`}
              >
                {plan.hardware && (
                  <div className="absolute top-0 right-0 bg-[#ea7317] text-white font-mono font-black text-[6.5px] tracking-widest px-2.5 py-1 rounded-bl uppercase animate-pulse">
                    {(plan.baseId === 'diamond' || plan.baseId === 'black_diamond' || plan.baseId === 'vip_lifetime') 
                      ? '★ DEVICE & eSIM ALLOWANCE' 
                      : '★ GLOBAL eSIM INCLUDED'}
                  </div>
                )}

                <div className="space-y-4">
                  {/* Card Header details */}
                  <div className="flex items-center gap-3">
                    <span className="filter drop-shadow-sm group-hover:scale-105 transition-transform">{renderTierIcon(plan.iconName)}</span>
                    <div>
                      <h4 className="text-sm font-sans font-black text-white uppercase tracking-wider">{plan.tier}</h4>
                      <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">{plan.title}</p>
                    </div>
                  </div>

                  <p className="text-[10px] font-sans text-slate-400 min-h-[30px] leading-relaxed normal-case">
                    {plan.subtitle}
                  </p>

                  <div className="py-2 border-y border-slate-900 flex items-baseline gap-1 bg-black/40 px-3 rounded-lg">
                    <span className="text-2xl font-mono font-black text-white">
                      {(plan.baseId === 'diamond' || plan.baseId === 'platinum' || plan.baseId === 'black_diamond') 
                        ? 'Request Quote' 
                        : plan.priceStr}
                    </span>
                    {(plan.baseId !== 'diamond' && plan.baseId !== 'platinum' && plan.baseId !== 'black_diamond') && (
                      <span className="text-[9px] font-mono text-slate-500"> / Billed {billingCycle.toLowerCase()}</span>
                    )}
                  </div>

                  {/* Operational configurations */}
                  <div className="space-y-1.5 font-mono text-[9px] bg-slate-950/60 p-2.5 rounded-xl border border-slate-900 leading-normal">
                    <div className="text-[7.5px] text-cyan-400 font-extrabold uppercase tracking-widest mb-1">COMPRESSED GRID OVERHEADS:</div>
                    <div className="flex justify-between text-slate-300">
                      <span>Max Deployed Workers:</span>
                      <strong className="text-white">{plan.maxAgents} Slots</strong>
                    </div>
                    <div className="flex justify-between text-slate-300">
                      <span>Monthly Task Limits:</span>
                      <strong className="text-white">{plan.maxTasks.toLocaleString()} Runs</strong>
                    </div>
                    <div className="flex justify-between text-slate-300 mt-1 border-t border-slate-900/60 pt-1.5">
                      <span>Mapped Maintenance:</span>
                      <strong className="text-[#e840f3]">{plan.mappedMaint}</strong>
                    </div>
                    {plan.techAddon && (
                      <div className="flex justify-between text-slate-300 mt-1 border-t border-slate-900/60 pt-1.5">
                        <span>AI &amp; Driver Tech:</span>
                        <strong className="text-cyan-400">{plan.techAddon}</strong>
                      </div>
                    )}
                  </div>

                  {/* Included benefits list */}
                  <div className="space-y-2 pt-1">
                    <span className="text-[7.5px] font-mono text-slate-500 tracking-wider font-extrabold uppercase block">
                      INCLUDED MATRIX BENEFITS:
                    </span>
                    <ul className="space-y-1.5">
                      {plan.perks.map((feat) => {
                        const key = `${plan.id}-${feat}`;
                        const isHovered = hoveredFeatureKey === key;
                        return (
                          <li 
                            key={feat}
                            className="relative flex items-center justify-between text-[10.5px] font-mono text-slate-300 hover:text-white transition-all py-0.5 px-1 rounded-md hover:bg-white/5 cursor-help"
                            onMouseEnter={() => setHoveredFeatureKey(key)}
                            onMouseLeave={() => setHoveredFeatureKey(null)}
                          >
                            <div className="flex items-start gap-1.5 text-left leading-tight">
                              <CheckCircle size={10.5} className="text-cyan-400 shrink-0 mt-0.5" />
                              <span>{feat}</span>
                            </div>
                            <Info size={9.5} className="text-slate-500 shrink-0" />
                            <FeatureTooltip isHovered={isHovered} feature={feat} />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>

                {/* Confirm Active Button */}
                <div className="mt-5 pt-3.5 border-t border-slate-950 space-y-2">
                  {(() => {
                    if (plan.baseId === 'bronze' || plan.baseId === 'silver') {
                      const packageLink = plan.baseId === 'bronze' ? TIDE_PAYMENT_LINK_BRONZE_PACKAGE : TIDE_PAYMENT_LINK_SILVER_PACKAGE;
                      const qrImage = plan.baseId === 'bronze' ? '/payments/tide-qr-bronze-package.png' : '/payments/tide-qr-silver-package.png';
                      const label = plan.baseId === 'bronze' ? 'Buy Bronze' : 'Buy Silver';

                      const handleBuyClick = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        if (packageLink && packageLink.startsWith('http')) {
                          window.open(packageLink, '_blank', 'noopener,noreferrer');
                        } else {
                          playNexoraXSound(1200, 0.2);
                          triggerToast("Payment link is being prepared. Please contact NexoraX Digital for activation.", "info");
                        }
                      };

                      return (
                        <div className="space-y-2 w-full">
                          <button
                            onClick={handleBuyClick}
                            className="w-full py-2 bg-gradient-to-r from-cyan-400 to-[#e840f3] hover:shadow-[0_0_12px_rgba(0,255,255,0.3)] text-black font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center font-bold"
                          >
                            {label}
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedQrInfo({
                                  title: `NEXORAX ${plan.tier}`,
                                  price: plan.priceVal,
                                  image: qrImage,
                                  url: packageLink
                                });
                              }}
                              className="py-1.5 bg-slate-950 border border-slate-800 text-slate-300 hover:text-white font-mono text-[8px] uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center"
                            >
                              Scan QR
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChoosePlan(plan.id, plan.tier);
                                triggerToast(`Selected ${plan.tier}. Request add-ons below in the checkout workspace.`, "info");
                              }}
                              className="py-1.5 bg-transparent border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white font-mono text-[8px] uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center"
                            >
                              Request Add-ons
                            </button>
                          </div>
                        </div>
                      );
                    } else if (plan.baseId === 'gold') {
                      const handlePayDeposit = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        window.open(TIDE_PAYMENT_LINK_GOLD_DEPOSIT, '_blank', 'noopener,noreferrer');
                      };

                      return (
                        <div className="space-y-2 w-full">
                          <button
                            onClick={handlePayDeposit}
                            className="w-full py-2 bg-gradient-to-r from-cyan-400 to-[#e840f3] hover:shadow-[0_0_12px_rgba(0,255,255,0.3)] text-black font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center font-bold"
                          >
                            Pay Gold Deposit (£749.70)
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedQrInfo({
                                  title: `NEXORAX GOLD DEPOSIT`,
                                  price: 749.70,
                                  image: '/payments/tide-qr-gold-deposit.png',
                                  url: TIDE_PAYMENT_LINK_GOLD_DEPOSIT
                                });
                              }}
                              className="py-1.5 bg-slate-950 border border-slate-800 text-slate-300 hover:text-white font-mono text-[8px] uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center"
                            >
                              Scan QR
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChoosePlan(plan.id, plan.tier);
                                triggerToast("Gold Plan selected. Proceeding to custom quote/proposal request.", "info");
                              }}
                              className="py-1.5 bg-transparent border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white font-mono text-[8px] uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center"
                            >
                              Request Gold Quote
                            </button>
                          </div>
                          <p className="text-[7.5px] font-mono text-slate-500 uppercase tracking-wider text-center pt-0.5 leading-normal normal-case">
                            “Remaining balance is payable after final quote approval by secure invoice, bank transfer, or agreed payment schedule.”
                          </p>
                        </div>
                      );
                    } else {
                      // Diamond, Platinum, Black Diamond
                      const buttonLabel = `Request ${plan.tier} Quote`;
                      return (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChoosePlan(plan.id, plan.tier);
                            triggerToast(`Selected ${plan.tier}. Proceeding to custom quote/proposal request.`, "info");
                          }}
                          className="w-full py-2.5 bg-slate-900 hover:bg-slate-850 border border-slate-800 text-white font-mono text-[9px] font-black uppercase tracking-widest rounded-lg transition-all cursor-pointer text-center"
                        >
                          {buttonLabel}
                        </button>
                      );
                    }
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ----------------------------------------------------
          SECTION 5 — VIP LIFETIME ACCESS
          ---------------------------------------------------- */}
      <div className="max-w-7xl mx-auto px-6 w-full z-10">
        <div className="bg-gradient-to-r from-amber-600/35 via-cyan-950/20 to-amber-900/40 border-[2px] border-amber-500/40 rounded-[2.5rem] p-8 md:p-12 shadow-[0_0_40px_rgba(245,158,11,0.2)] text-left relative overflow-hidden group">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-500/5 via-transparent to-transparent pointer-events-none" />
          
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 relative z-10">
            <div className="space-y-4 max-w-2xl">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-950/80 border border-amber-500/40 rounded-full text-[9px] font-mono uppercase tracking-[0.2em] text-amber-300 font-extrabold animate-pulse">
                👑 Premium Package Benefits
              </div>
              <h3 className="text-3xl md:text-4xl font-sans font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-amber-200 to-amber-400 uppercase tracking-tight">
                CUSTOM ENTERPRISE & VIP SYSTEMS
              </h3>
              <p className="text-xs md:text-sm font-sans text-slate-300 leading-relaxed font-bold">
                For larger businesses needing custom websites, dashboards, portals, automation, mobile app-style systems, CRM tools, hosting support, AI chat/voice add-ons, driver/fleet solutions, and premium annual support packages.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 font-mono text-[10px] sm:text-xs text-white uppercase tracking-wider font-extrabold pt-2">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  <span>Custom digital system planning</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  <span>Website, dashboard, app-style portal or automation support</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  <span>Managed setup and maintenance options</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  <span>Premium annual package options</span>
                </div>
              </div>
            </div>

            <div className="bg-black/85 border border-amber-500/30 p-6 rounded-3xl text-center lg:text-right shrink-0 w-full lg:w-96 space-y-4">
              <div>
                <span className="text-[9.5px] font-mono text-zinc-500 uppercase tracking-widest font-black block">ONE-TIME DIRECT INVESTMENT</span>
                <span className="text-3xl md:text-4xl font-mono font-black text-amber-300 tracking-tight">Request Quote</span>
                <span className="text-[#ea7317] text-xs font-mono font-bold block mt-1">/ Custom Enterprise Package</span>
                <span className="text-[8.5px] font-mono text-slate-400 block mt-1">
                  + Maintenance: Custom Enterprise Maintenance Contract
                </span>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-400/30 p-3 rounded-xl text-left">
                <span className="block font-mono text-[8px] uppercase tracking-wide font-black text-amber-400">HARDWARE & CONNECTIVITY ALLOWANCE</span>
                <span className="block font-sans text-[10.5px] text-amber-100/90 leading-tight">
                  Subscription-linked mobile device allowance + unlimited global eSIM support.
                  <br /><br />
                  Available on approved Diamond, Black Diamond and VIP packages while the subscription remains active and paid.
                  <br /><br />
                  If the subscription is cancelled, unpaid, downgraded or terminated, the device/eSIM benefit ends. Any supplied device must be returned to NexoraX Digital LTD or settled under the signed package agreement.
                  <br /><br />
                  Subject to final quote, supplier availability, payment approval, package approval and written agreement.
                </span>
              </div>

              <button 
                onClick={() => handleChoosePlan('tvip_lifetime', 'VIP Lifetime Access')}
                className="w-full py-3 bg-gradient-to-r from-amber-400 to-amber-600 text-black font-sans font-black text-[10.5px] uppercase tracking-widest rounded-xl hover:shadow-[0_0_20px_rgba(245,158,11,0.5)] transition-all cursor-pointer text-center border-t border-white/20"
              >
                {selectedPlan === 'tvip_lifetime' ? 'REQUESTING VIP QUOTE / STRATEGY CALL' : 'REQUEST VIP QUOTE / STRATEGY CALL'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------
          SECTION 6 — PROJECT DEPLOYMENT PLANS
          ---------------------------------------------------- */}
      <div className="max-w-7xl mx-auto px-6 w-full space-y-6 scroll-mt-24" id="maintenance-section">
        <div className="flex flex-col items-center text-center space-y-1">
          <h3 className="text-sm font-black font-mono text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
            <Layers className="w-4 h-4 text-cyan-400" />
            Approved Staged Payment Plans
          </h3>
          <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
            Clear setup costs and scale comfortably under secure B2B billing options
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {maintenancePlans.map((stg) => (
            <div 
              key={stg.id}
              className="bg-[#050914] border-2 border-slate-800 rounded-3xl p-6 text-left flex flex-col justify-between hover:border-cyan-500/40 hover:shadow-[0_0_20px_rgba(0,255,255,0.05)] transition-all group"
            >
              <div className="space-y-4">
                <div className="flex justify-between items-center text-left">
                  <span className="font-sans font-black text-white text-lg uppercase tracking-wider">{stg.name}</span>
                  <div className="text-right">
                    <span className="text-xl font-mono font-black text-cyan-300 block">£{stg.price.toFixed(2)}/mo</span>
                    <span className="text-[7.5px] text-slate-500 block">MONTHLY MAINTENANCE</span>
                  </div>
                </div>

                <p className="text-[10.5px] font-sans text-slate-300 normal-case leading-relaxed">
                  {stg.desc}
                </p>

                <div className="bg-slate-900/60 p-2.5 rounded border border-slate-850/80 text-[8.5px] font-mono text-amber-400 italic">
                  💡 {stg.alternative}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-950">
                {(() => {
                  let buttonLabel = '';
                  let paymentLink = '';
                  let qrCodeImage = '';

                  if (stg.id === 'maintenance_starter') {
                    buttonLabel = 'Start Starter Maintenance';
                    paymentLink = TIDE_PAYMENT_LINK_STARTER_MAINTENANCE;
                    qrCodeImage = '/payments/tide-qr-starter-maintenance.png';
                  } else if (stg.id === 'maintenance_premium') {
                    buttonLabel = 'Start Premier Maintenance';
                    paymentLink = TIDE_PAYMENT_LINK_PREMIER_MAINTENANCE;
                    qrCodeImage = '/payments/tide-qr-premier-maintenance.png';
                  } else if (stg.id === 'maintenance_pro') {
                    buttonLabel = 'Start Pro Maintenance';
                    paymentLink = TIDE_PAYMENT_LINK_PRO_MAINTENANCE;
                    qrCodeImage = '/payments/tide-qr-pro-maintenance.png';
                  }

                  const handleButtonClick = () => {
                    window.open(paymentLink, '_blank', 'noopener,noreferrer');
                  };

                  return (
                    <div className="space-y-2 w-full">
                      <button
                        onClick={handleButtonClick}
                        className="w-full py-2 bg-gradient-to-r from-cyan-400 to-[#e840f3] hover:shadow-[0_0_12px_rgba(0,255,255,0.3)] text-black font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center font-bold"
                      >
                        {buttonLabel}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedQrInfo({
                            title: stg.name.toUpperCase(),
                            price: stg.price,
                            image: qrCodeImage,
                            url: paymentLink
                          });
                        }}
                        className="w-full py-1.5 bg-slate-950/60 hover:bg-slate-900 border border-slate-800 text-slate-300 hover:text-white font-mono text-[9px] uppercase tracking-wider rounded-lg transition-all cursor-pointer text-center"
                      >
                        Scan QR
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ----------------------------------------------------
          SECTION 7 — PLAN ENTITLEMENT COMPREHENSIVE MATRIX
          ---------------------------------------------------- */}
      <div className="max-w-7xl mx-auto px-6 w-full space-y-6">
        <div className="flex flex-col items-center text-center space-y-1">
          <h3 className="text-sm font-black font-mono text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
            <Layers className="w-4 h-4 text-cyan-400 animate-pulse" />
            Plan Entitlement Matrix
          </h3>
          <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
            Side-by-side technical breakdown of all 6 subscription tiers
          </p>
        </div>

        <div className="bg-black/85 border border-slate-800 rounded-3xl p-1.5 sm:p-3 overflow-x-auto shadow-xl z-20">
          <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse min-w-[700px]">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 uppercase text-[9px] tracking-wider bg-[#040713]">
                <th className="p-4 font-bold">FEAUTURES & GRID SPEC</th>
                <th className="p-4 text-orange-400">BRONZE</th>
                <th className="p-4 text-slate-300">SILVER</th>
                <th className="p-4 text-yellow-400">GOLD</th>
                <th className="p-4 text-cyan-300">DIAMOND</th>
                <th className="p-4 text-teal-200">PLATINUM</th>
                <th className="p-4 text-purple-400">BLACK DIAMOND</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-900 uppercase">
              <tr>
                <td className="p-4 font-bold text-white normal-case">Sector Grid Slots</td>
                <td className="p-4 text-slate-400">Node-01 & Node-06</td>
                <td className="p-4 text-slate-400">First 6 Sectors</td>
                <td className="p-4 text-slate-300">First 12 Sectors</td>
                <td className="p-4 text-cyan-200 font-extrabold">Full Site & Services</td>
                <td className="p-4 text-teal-200 font-extrabold">Full Site + Custom App</td>
                <td className="p-4 text-purple-300 font-extrabold">Full Enterprise Suite</td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-white normal-case">Deployed AI Workers</td>
                <td className="p-4">2 slots</td>
                <td className="p-4">10 slots</td>
                <td className="p-4">35 slots</td>
                <td className="p-4">120 slots</td>
                <td className="p-4">350 slots</td>
                <td className="p-4 text-purple-400 font-black">999 slots</td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-white normal-case">Premium Connectors</td>
                <td className="p-4 text-slate-600">✕ None</td>
                <td className="p-4 text-slate-400">3 basic</td>
                <td className="p-4 text-[#cbd5e1]">10 premium</td>
                <td className="p-4 text-[#cbd5e1]">50 Advanced</td>
                <td className="p-4 text-teal-300 font-extrabold">Unlimited</td>
                <td className="p-4 text-purple-300 font-extrabold">Direct Dev Access</td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-white normal-case">Telemetry Updates</td>
                <td className="p-4">Daily Sync</td>
                <td className="p-4">Hourly</td>
                <td className="p-4">5-Minute Intervals</td>
                <td className="p-4 text-cyan-300">Real-time</td>
                <td className="p-4 text-teal-300">High-speed Sync</td>
                <td className="p-4 text-purple-300">Real-time Enterprise Sync</td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-white normal-case">Monthly Execution limits</td>
                <td className="p-4">1k Runs</td>
                <td className="p-4">5k Runs</td>
                <td className="p-4">25k Runs</td>
                <td className="p-4">150k Runs</td>
                <td className="p-4">1m Runs</td>
                <td className="p-4 text-purple-400 font-black">Unlimited</td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-white normal-case">Compliance Sponsor Priority Desk</td>
                <td className="p-4 text-slate-600">✕ None</td>
                <td className="p-4 text-slate-600">✕ None</td>
                <td className="p-4">Standard Delivery</td>
                <td className="p-4 text-[#cbd5e1]">Priority Support</td>
                <td className="p-4 text-teal-300">Express Legal Team</td>
                <td className="p-4 text-purple-300">Direct Priority Support</td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-white normal-case">Direct bank discount</td>
                <td className="p-4 text-slate-500">2%</td>
                <td className="p-4 text-slate-400">3%</td>
                <td className="p-4 text-amber-400 font-extrabold">5% B2B REDUCTION</td>
                <td className="p-4 text-amber-400 font-extrabold">5% B2B REDUCTION</td>
                <td className="p-4 text-emerald-400 font-black">10% DIRECT BANK</td>
                <td className="p-4 text-emerald-400 font-black">15% DIRECT BANK</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ----------------------------------------------------
          SECTION 8 — SECURE PAYMENT PORTAL
          ---------------------------------------------------- */}
      <div 
        className="max-w-7xl mx-auto px-6 w-full space-y-10 scroll-mt-24"
        id="secure-payment-portal-element"
      >
        <div className="flex flex-col items-center text-center space-y-1">
          <h3 className="text-sm font-black font-mono text-cyan-400 uppercase tracking-widest flex items-center gap-1.5 animate-pulse">
            <span className="w-2.5 h-2.5 rounded bg-cyan-400 animate-ping"></span>
            Secure Payment Portal
          </h3>
          <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
            Verified direct checkout gateways compliant with SC887095 limits
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* TABS & FORM PANEL (Left - Col range 7) */}
          <div className="lg:col-span-7 bg-black/90 border-2 border-slate-800 rounded-3xl p-6 space-y-6 text-left shadow-xl relative z-10">
            
            {/* Tab selection bar */}
            <div className="flex border-b border-slate-900 pb-3 gap-2">
              <button
                onClick={() => {
                  setPaymentPortalTab('CHECKOUT');
                  playNexoraXSound(400, 0.15);
                }}
                className={`flex-1 py-2.5 px-3 rounded-lg font-mono text-[9px] font-black uppercase tracking-widest transition-all cursor-pointer border ${paymentPortalTab === 'CHECKOUT' ? 'bg-cyan-500/10 text-[#00ffff] border-cyan-500/30' : 'bg-transparent text-slate-500 border-transparent hover:text-white'}`}
              >
                1. Standard Checkout
              </button>
              <button
                onClick={() => {
                  setPaymentPortalTab('INVOICE');
                  playNexoraXSound(450, 0.15);
                }}
                className={`flex-1 py-2.5 px-3 rounded-lg font-mono text-[9px] font-black uppercase tracking-widest transition-all cursor-pointer border ${paymentPortalTab === 'INVOICE' ? 'bg-cyan-500/10 text-[#00ffff] border-cyan-500/30' : 'bg-transparent text-slate-500 border-transparent hover:text-white'}`}
              >
                2. Request Secure Invoice
              </button>
              <button
                onClick={() => {
                  setPaymentPortalTab('CONTACT');
                  playNexoraXSound(500, 0.15);
                }}
                className={`flex-1 py-2.5 px-3 rounded-lg font-mono text-[9px] font-black uppercase tracking-widest transition-all cursor-pointer border ${paymentPortalTab === 'CONTACT' ? 'bg-cyan-500/10 text-[#00ffff] border-cyan-500/30' : 'bg-transparent text-slate-500 border-transparent hover:text-white'}`}
              >
                3. Contact Billing
              </button>
            </div>

            {/* TERMS & POLICIES COMMON CONTAINER (CHECKOUT or INVOICE tabs) */}
            {paymentPortalTab !== 'CONTACT' && (
              <div className="space-y-6 border-b border-slate-900 pb-5">
                {/* A. REVIEW & ACCEPT TERMS PANEL */}
                <div className="p-5 bg-gradient-to-b from-purple-950/20 to-black/80 border-2 border-[#e840f3]/40 rounded-2xl space-y-4 shadow-[0_0_25px_rgba(232,64,243,0.15)] relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-[#00ffff]/5 rounded-full blur-2xl pointer-events-none" />
                  
                  <div className="flex items-center gap-2 border-b border-slate-900 pb-2">
                    <ShieldCheck className="w-5 h-5 text-cyan-400 animate-pulse" />
                    <h4 className="text-xs font-mono font-black text-white uppercase tracking-widest">Review & Accept Terms</h4>
                  </div>
                  
                  <p className="text-[10px] text-slate-400 font-sans leading-normal normal-case">
                    Before a client can continue to secure checkout or submit a direct invoice request, they must clearly accept individual legal disclosures regarding service hosting and local rules.
                  </p>

                  <div className="space-y-3 pt-1">
                    {/* Checkbox 1 */}
                    <div className="flex items-start gap-2.5">
                      <input 
                        type="checkbox"
                        id="chk-plan"
                        checked={chkPlan}
                        onChange={(e) => setChkPlan(e.target.checked)}
                        className="mt-0.5 accent-cyan-400 w-3.5 h-3.5 cursor-pointer shrink-0 rounded"
                      />
                      <label htmlFor="chk-plan" className="text-[9.5px] font-mono text-slate-300 leading-snug cursor-pointer select-none">
                        I understand the selected NexoraX plan ({activePlanObject.tier}), billing cycle ({billingCycle}), and total price shown before checkout.
                      </label>
                    </div>

                    {/* Checkbox 2 */}
                    <div className="flex items-start gap-2.5">
                      <input 
                        type="checkbox"
                        id="chk-maint"
                        checked={chkMaint}
                        onChange={(e) => setChkMaint(e.target.checked)}
                        className="mt-0.5 accent-cyan-400 w-3.5 h-3.5 cursor-pointer shrink-0 rounded"
                      />
                      <label htmlFor="chk-maint" className="text-[9.5px] font-mono text-slate-300 leading-snug cursor-pointer select-none normal-case">
                        {adminMaintenanceFeeWording}
                      </label>
                    </div>

                    {/* Checkbox 3 */}
                    <div className="flex items-start gap-2.5">
                      <input 
                        type="checkbox"
                        id="chk-maint-stop"
                        checked={chkMaintStop}
                        onChange={(e) => setChkMaintStop(e.target.checked)}
                        className="mt-0.5 accent-cyan-400 w-3.5 h-3.5 cursor-pointer shrink-0 rounded"
                      />
                      <label htmlFor="chk-maint-stop" className="text-[9.5px] font-mono text-slate-300 leading-snug cursor-pointer select-none">
                        I understand that the Maintenance & Hosting Fee stops when the active NexoraX-hosted service is cancelled, terminated, or no longer provided.
                      </label>
                    </div>

                    {/* Checkbox 4 */}
                    <div className="flex items-start gap-2.5">
                      <input 
                        type="checkbox"
                        id="chk-vat"
                        checked={chkVat}
                        onChange={(e) => setChkVat(e.target.checked)}
                        className="mt-0.5 accent-cyan-400 w-3.5 h-3.5 cursor-pointer shrink-0 rounded"
                      />
                      <label htmlFor="chk-vat" className="text-[9.5px] font-mono text-slate-300 leading-snug cursor-pointer select-none">
                        {adminVatWording}
                      </label>
                    </div>

                    {/* Checkbox 5 */}
                    <div className="flex items-start gap-2.5">
                      <input 
                        type="checkbox"
                        id="chk-comm"
                        checked={chkComm}
                        onChange={(e) => setChkComm(e.target.checked)}
                        className="mt-0.5 accent-cyan-400 w-3.5 h-3.5 cursor-pointer shrink-0 rounded"
                      />
                      <label htmlFor="chk-comm" className="text-[9.5px] font-mono text-slate-300 leading-snug cursor-pointer select-none">
                        {adminCommunityAllocationWording}
                      </label>
                    </div>

                    {/* Checkbox 6 */}
                    <div className="flex items-start gap-2.5">
                      <input 
                        type="checkbox"
                        id="chk-dif"
                        checked={chkDifferent}
                        onChange={(e) => setChkDifferent(e.target.checked)}
                        className="mt-0.5 accent-cyan-400 w-3.5 h-3.5 cursor-pointer shrink-0 rounded"
                      />
                      <label htmlFor="chk-dif" className="text-[9.5px] font-mono text-slate-300 leading-snug cursor-pointer select-none">
                        I understand that access passes, subscriptions, deployments, and custom services may have different cancellation and refund rules.
                      </label>
                    </div>

                    {/* Checkbox 7 */}
                    <div className="flex items-start gap-2.5">
                      <input 
                        type="checkbox"
                        id="chk-agree"
                        checked={chkAgree}
                        onChange={(e) => setChkAgree(e.target.checked)}
                        className="mt-0.5 accent-cyan-400 w-3.5 h-3.5 cursor-pointer shrink-0 rounded"
                      />
                      <label htmlFor="chk-agree" className="text-[9.5px] font-mono text-slate-300 leading-snug cursor-pointer select-none">
                        {adminGeneralTermsWording} <a href={adminTermsOfServiceLink} target="_blank" rel="noopener noreferrer" className="text-[#00ffff] font-extrabold hover:underline">Terms of Service</a>, <a href={adminPrivacyPolicyLink} target="_blank" rel="noopener noreferrer" className="text-[#00ffff] font-extrabold hover:underline">Privacy Policy</a>, <a href="#refund" onClick={(e) => { e.preventDefault(); setShowFullRefundModal(true); }} className="text-[#00ffff] font-extrabold hover:underline">Refund Policy</a>, and <a href="#cancellation" onClick={(e) => { e.preventDefault(); setShowFullCancelModal(true); }} className="text-[#00ffff] font-extrabold hover:underline">Cancellation Policy</a>.
                      </label>
                    </div>

                    {/* STAGED PAYMENT CONFIGURATION CHECKBOX */}
                    {(selectedPlan === 'stage_presence' || selectedPlan === 'stage_enterprise') && (
                      <div className="p-3.5 bg-cyan-950/20 border border-cyan-500/30 rounded-xl space-y-2.5 mt-3">
                        <strong className="text-[9px] font-mono text-cyan-400 uppercase tracking-widest block font-black">Approved staged payment plan Terms</strong>
                        <p className="text-[9.5px] text-slate-400 leading-relaxed font-sans normal-case">
                          {adminStagedWording}
                        </p>
                        <div className="flex items-start gap-2.5 pt-1">
                          <input 
                            type="checkbox"
                            id="chk-staged-spec"
                            checked={chkStaged}
                            onChange={(e) => setChkStaged(e.target.checked)}
                            className="mt-0.5 accent-cyan-400 w-3.5 h-3.5 cursor-pointer shrink-0"
                          />
                          <label htmlFor="chk-staged-spec" className="text-[9.5px] font-mono text-cyan-300 leading-snug cursor-pointer font-bold select-none uppercase">
                            I understand that staged payment access, service delivery, cancellation, and ownership terms must be confirmed in writing before activation.
                          </label>
                        </div>
                      </div>
                    )}

                    {/* VIP LIFETIME CONFIGURATION CHECKBOX */}
                    {(selectedPlan === 'tvip_lifetime' || selectedPlan === 't_vip_lifetime' || selectedPlan === 'vip_lifetime') && (
                      <div className="p-3.5 bg-purple-950/20 border border-purple-500/30 rounded-xl space-y-2.5 mt-3">
                        <strong className="text-[9px] font-mono text-[#f574fe] uppercase tracking-widest block font-black">VIP Lifetime Access Terms</strong>
                        <p className="text-[9.5px] text-slate-400 leading-relaxed font-sans normal-case">
                          {adminVipWording}
                        </p>
                        <div className="flex items-start gap-2.5 pt-1">
                          <input 
                            type="checkbox"
                            id="chk-vip-spec"
                            checked={chkVip}
                            onChange={(e) => setChkVip(e.target.checked)}
                            className="mt-0.5 accent-[#e840f3] w-3.5 h-3.5 cursor-pointer shrink-0"
                          />
                          <label htmlFor="chk-vip-spec" className="text-[9.5px] font-mono text-purple-300 leading-snug cursor-pointer font-bold select-none uppercase">
                            I understand that VIP Lifetime Access does not grant ownership of NexoraX source code, backend infrastructure, private systems, admin credentials, or third-party services unless explicitly agreed in writing.
                          </label>
                        </div>
                      </div>
                    )}

                    {/* INVOICE RULES SPECIFIC CHECKBOXES */}
                    {paymentPortalTab === 'INVOICE' && (
                      <div className="p-3.5 bg-rose-950/20 border border-rose-500/30 rounded-xl space-y-2.5 mt-3">
                        <strong className="text-[9px] font-mono text-rose-400 uppercase tracking-widest block font-black">Invoice Request Terms</strong>
                        
                        <div className="space-y-2.5 pt-1">
                          <div className="flex items-start gap-2.5">
                            <input 
                              type="checkbox"
                              id="chk-inv-a"
                              checked={chkInvoiceA}
                              onChange={(e) => setChkInvoiceA(e.target.checked)}
                              className="mt-0.5 accent-rose-400 w-3.5 h-3.5 cursor-pointer shrink-0"
                            />
                            <label htmlFor="chk-inv-a" className="text-[9.5px] font-mono text-rose-300 leading-snug cursor-pointer select-none">
                              {adminInvoiceWordingA}
                            </label>
                          </div>
                          
                          <div className="flex items-start gap-2.5">
                            <input 
                              type="checkbox"
                              id="chk-inv-b"
                              checked={chkInvoiceB}
                              onChange={(e) => setChkInvoiceB(e.target.checked)}
                              className="mt-0.5 accent-rose-400 w-3.5 h-3.5 cursor-pointer shrink-0"
                            />
                            <label htmlFor="chk-inv-b" className="text-[9.5px] font-mono text-rose-300 leading-snug cursor-pointer select-none">
                              {adminInvoiceWordingB}
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* B. DETAILED REFUND & CANCELLATION SUMMARIES */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Refund Policy Summary Card */}
                  <div className="p-4 bg-slate-950/80 border border-slate-900 rounded-2xl flex flex-col justify-between space-y-3 text-left">
                    <div className="space-y-2">
                       <div className="flex items-center gap-1.5 border-b border-slate-900 pb-1.5">
                        <HelpCircle className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                        <h5 className="text-[10px] font-mono font-black text-white uppercase tracking-wider">Refund Policy Summary</h5>
                      </div>
                      <p className="text-[10px] text-slate-400 leading-relaxed font-sans normal-case">
                        {adminRefundPolicySummary}
                      </p>
                      
                      <div className="space-y-1 text-[9px] font-mono uppercase bg-black/40 p-2 rounded-lg border border-slate-900">
                        <div className="flex justify-between">
                          <span className="text-slate-500 font-bold">Access Passes:</span>
                          <span className="text-slate-300">Limited once active</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500 font-bold">Monthly Subs:</span>
                          <span className="text-slate-300">Cancel renewals</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500 font-bold">Yearly plans:</span>
                          <span className="text-slate-300">Terms governed</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500 font-bold">Deployments:</span>
                          <span className="text-cyan-400 font-bold font-black">Unrefundable setup</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500 font-bold">Enterprise/VIP:</span>
                          <span className="text-white font-bold font-black">Per contract</span>
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      type="button"
                      onClick={() => {
                        playNexoraXSound(550, 0.15);
                        setShowFullRefundModal(true);
                      }}
                      className="w-full py-2 bg-slate-900 hover:bg-slate-850 hover:text-white text-slate-400 text-[8.5px] font-mono font-black uppercase rounded-lg border border-slate-800 cursor-pointer transition-colors"
                    >
                      View Full Refund Policy
                    </button>
                  </div>

                  {/* Cancellation Policy Summary Card */}
                  <div className="p-4 bg-slate-950/80 border border-slate-900 rounded-2xl flex flex-col justify-between space-y-3 text-left">
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5 border-b border-slate-900 pb-1.5">
                        <X className="w-3.5 h-3.5 text-cyan-400" />
                        <h5 className="text-[10px] font-mono font-black text-white uppercase tracking-wider">Cancellation Summary</h5>
                      </div>
                      <p className="text-[10px] text-slate-400 leading-relaxed font-sans normal-case">
                        {adminCancellationPolicySummary}
                      </p>
                      
                      <div className="space-y-1 pl-2 border-l border-purple-500/20 py-0.5 text-[8.5px] font-mono text-slate-500 uppercase">
                        <div>• Cancel subscription renewal</div>
                        <div>• Request service termination</div>
                        <div>• Download data where available</div>
                        <div>• Confirm final invoice status</div>
                        <div>• Disable active automation</div>
                        <div>• Stop fee when service ends</div>
                      </div>
                    </div>
                    
                    <button 
                      type="button"
                      onClick={() => {
                        playNexoraXSound(600, 0.2);
                        setShowFullCancelModal(true);
                        triggerToast(`★ Cancellation desk compiled. Notice directed to: ${adminCancellationRouting}`, "info");
                      }}
                      className="w-full py-2 bg-slate-900 hover:bg-slate-850 hover:text-white text-slate-400 text-[8.5px] font-mono font-black uppercase rounded-lg border border-slate-800 cursor-pointer transition-colors"
                    >
                      Request Cancellation
                    </button>
                  </div>
                </div>

                {/* C. SERVICE ACCESS WARNING BANNER */}
                <div className="p-4 bg-red-950/10 border border-red-500/20 rounded-2xl text-[10px] leading-relaxed text-slate-400 font-sans normal-case flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5 animate-pulse" />
                  <p>
                    <span className="font-mono text-[9px] font-black uppercase text-red-400 block mb-0.5 tracking-wider">Service Access Advisory Note:</span>
                    If a NexoraX-hosted service, managed website, AI automation system, or platform workflow is cancelled, access to hosted infrastructure and managed services may stop after the active paid term ends.
                  </p>
                </div>
              </div>
            )}

            {/* TAB CONTAINER 1: SECURE GATEWAY CHECKOUT LINKS */}
            {paymentPortalTab === 'CHECKOUT' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-xs font-mono font-black text-white uppercase tracking-wide">SECURE REDIRECT GATEWAYS</h4>
                  <p className="text-[10px] text-slate-400 leading-normal pt-1 normal-case font-sans">
                    All payment routers compile with zero-fraction. Choose the absolute billing pipeline matching your corporate settlement parameters.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* SecurePayment Link */}
                  {canCheckout ? (
                    <a 
                      href="https://pay.SecurePayment.co/pay/9fd1aa7e-20b1-44b0-bf57-b75944dd5134"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => handlePaymentLinkClick(e, "Bank Transfer")}
                      className="p-4 bg-slate-950 hover:bg-cyan-950/20 border border-slate-800 hover:border-cyan-400 rounded-2xl transition-all group/link text-left relative overflow-hidden"
                    >
                      <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-emerald-950 border border-emerald-500/30 font-mono text-[6.5px] font-extrabold text-emerald-400 rounded uppercase">
                        -5% Bank Discount
                      </div>
                      <div className="flex items-center gap-2 mb-2 text-white font-sans font-black uppercase text-xs">
                        Bank Transfer
                      </div>
                      <span className="block text-[8px] font-mono text-cyan-400 uppercase tracking-widest font-extrabold select-all">SecurePayment Settlement Deck</span>
                      <p className="text-[9.5px] text-slate-300 leading-snug pt-1 normal-case font-sans">
                        Route direct bank-wire via verified SecurePayment clearing system:<br/>
                        <span className="text-cyan-400 font-mono font-extrabold tracking-wider bg-slate-900 border border-slate-800 rounded px-1.5 py-0.5 text-[8.5px]">SECURE_PAYMENT_PROVIDER_CONNECTED</span>
                      </p>
                    </a>
                  ) : (
                    <div className="p-4 bg-slate-900/30 border border-slate-900 rounded-2xl opacity-40 cursor-not-allowed text-left relative overflow-hidden select-none">
                      <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-slate-950 border border-slate-850 font-mono text-[6.5px] font-extrabold text-slate-500 rounded uppercase">
                        Accept Terms to Unlock
                      </div>
                      <div className="flex items-center gap-2 mb-2 text-slate-500 font-sans font-black uppercase text-xs">
                        Bank Transfer
                      </div>
                      <span className="block text-[8px] font-mono text-slate-600 tracking-widest font-extrabold uppercase">SecurePayment Settlement Deck</span>
                      <p className="text-[9.5px] text-slate-500 leading-snug pt-1 normal-case font-sans">
                        Please review and accept terms above to activate direct bank transfer.
                      </p>
                    </div>
                  )}

                  {/* Stripe Link */}
                  {canCheckout ? (
                    <a 
                      href="https://buy.stripe.com/eVq9AM0kX8kU1Yz0CWaMU01"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => handlePaymentLinkClick(e, "Stripe")}
                      className="p-4 bg-slate-950 hover:bg-cyan-950/20 border border-slate-800 hover:border-cyan-400 rounded-2xl transition-all group/link text-left"
                    >
                      <div className="flex items-center gap-2 mb-2 text-white font-sans font-black uppercase text-xs">
                        Card / Stripe
                      </div>
                      <span className="block text-[8px] font-mono text-cyan-400 uppercase tracking-widest font-extrabold select-all">Standard Checkout Deck</span>
                      <p className="text-[9.5px] text-slate-400 leading-snug pt-1 normal-case font-sans min-h-[30px]">
                        Instant deployment using verified global credit/debit mechanisms.
                      </p>
                    </a>
                  ) : (
                    <div className="p-4 bg-slate-900/30 border border-slate-900 rounded-2xl opacity-40 cursor-not-allowed text-left select-none">
                      <div className="flex items-center gap-2 mb-2 text-slate-500 font-sans font-black uppercase text-xs">
                        Card / Stripe
                      </div>
                      <span className="block text-[8px] font-mono text-slate-600 tracking-widest font-extrabold uppercase">Standard Checkout Deck</span>
                      <p className="text-[9.5px] text-slate-500 leading-snug pt-1 normal-case font-sans min-h-[30px]">
                        Please accept terms checklists above to unlock standard card secure gateways.
                      </p>
                    </div>
                  )}

                  {/* PayPal Link */}
                  {canCheckout ? (
                    <a 
                      href="https://www.paypal.com/ncp/payment/SA88S26X4C8DJ"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => handlePaymentLinkClick(e, "PayPal")}
                      className="p-4 bg-slate-950 hover:bg-cyan-950/20 border border-slate-800 hover:border-cyan-400 rounded-2xl transition-all group/link text-left"
                    >
                      <div className="flex items-center gap-2 mb-2 text-white font-sans font-black uppercase text-xs">
                        PayPal Portal
                      </div>
                      <span className="block text-[8px] font-mono text-cyan-400 uppercase tracking-widest font-extrabold select-all">Alternative Settlement Node</span>
                      <p className="text-[9.5px] text-slate-400 leading-snug pt-1 normal-case font-sans min-h-[30px]">
                        Secure PayPal electronic transaction network anchoring.
                      </p>
                    </a>
                  ) : (
                    <div className="p-4 bg-slate-900/30 border border-slate-900 rounded-2xl opacity-40 cursor-not-allowed text-left select-none">
                      <div className="flex items-center gap-2 mb-2 text-slate-500 font-sans font-black uppercase text-xs">
                        PayPal Portal
                      </div>
                      <span className="block text-[8px] font-mono text-slate-600 tracking-widest font-extrabold uppercase">Alternative Settlement Node</span>
                      <p className="text-[9.5px] text-slate-500 leading-snug pt-1 normal-case font-sans min-h-[30px]">
                        Accept terms above to unlock alternative direct PayPal routes.
                      </p>
                    </div>
                  )}

                  {/* GoCardless */}
                  {canCheckout ? (
                    <a 
                      href="https://pay.gocardless.com/BRT01KSQFQ82TKX0K6PZWSYG6S8TJ"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => handlePaymentLinkClick(e, "GoCardless")}
                      className="p-4 bg-slate-950 hover:bg-cyan-950/20 border border-slate-800 hover:border-cyan-400 rounded-2xl transition-all group/link text-left"
                    >
                      <div className="flex items-center gap-2 mb-2 text-white font-sans font-black uppercase text-xs font-bold">
                        Direct Debit
                      </div>
                      <span className="block text-[8px] font-mono text-cyan-400 uppercase tracking-widest font-extrabold select-all">GoCardless Secure Deck</span>
                      <p className="text-[9.5px] text-slate-400 leading-snug pt-1 normal-case font-sans min-h-[30px]">
                        Configure monthly direct-debit settlements easily.
                      </p>
                    </a>
                  ) : (
                    <div className="p-4 bg-slate-900/30 border border-slate-900 rounded-2xl opacity-40 cursor-not-allowed text-left select-none">
                      <div className="flex items-center gap-2 mb-2 text-slate-500 font-sans font-black uppercase text-xs font-bold">
                        Direct Debit
                      </div>
                      <span className="block text-[8px] font-mono text-slate-600 tracking-widest font-extrabold uppercase">GoCardless Secure Deck</span>
                      <p className="text-[9.5px] text-slate-500 leading-snug pt-1 normal-case font-sans min-h-[30px]">
                        Accept terms above to configure secure Direct Debit recurring parameters.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB CONTAINER 2: REQUEST SECURE INVOICE */}
            {paymentPortalTab === 'INVOICE' && (
              <form onSubmit={handleRequestInvoice} className="space-y-4">
                <div>
                  <h4 className="text-xs font-mono font-black text-white uppercase tracking-wide animate-pulse">SECURE INVOICING HANDSHAKE</h4>
                  <p className="text-[10px] text-slate-400 leading-normal pt-1 normal-case font-sans">
                    Draft, register, and sync PDF billing files directly to your corporation's legal entities. All registered deals duplicate to GDrive vault.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[8.5px] font-mono text-slate-400 font-extrabold uppercase tracking-wide">Company Name</label>
                    <input 
                      required
                      type="text"
                      value={invoiceCompanyName}
                      onChange={(e) => setInvoiceCompanyName(e.target.value)}
                      placeholder="e.g. NexoraX Digital Ltd"
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs font-mono text-white focus:border-cyan-400 outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[8.5px] font-mono text-slate-400 font-extrabold uppercase tracking-wide">Billing Email</label>
                    <input 
                      required
                      type="email"
                      value={invoiceEmail}
                      onChange={(e) => setInvoiceEmail(e.target.value)}
                      placeholder="e.g. finance@nexorax.co.uk"
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs font-mono text-white focus:border-cyan-400 outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[8.5px] font-mono text-slate-400 font-extrabold uppercase tracking-wide">Billing Address</label>
                  <input 
                    required
                    type="text"
                    value={invoiceAddress}
                    onChange={(e) => setInvoiceAddress(e.target.value)}
                    placeholder="e.g. SC887095 Imperial Precinct, Dunfermline"
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs font-mono text-white focus:border-cyan-400 outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[8.5px] font-mono text-slate-400 font-extrabold uppercase tracking-wide">Campaign Reference Code (Optional)</label>
                    <input 
                      type="text"
                      value={invoiceReference}
                      onChange={(e) => setInvoiceReference(e.target.value)}
                      placeholder="e.g. NX-CAMP-2026"
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs font-mono text-white focus:border-cyan-400 outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[8.5px] font-mono text-slate-400 font-extrabold uppercase tracking-wide">Selected Plan Option</label>
                    <div className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5 text-xs font-mono text-cyan-300 font-black">
                      {activePlanObject.tier} Plan ({activePlanObject.priceStr})
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  {/* Secure checkout button is bright neon cyan (disabled if checkboxes are not checked) */}
                  {(chkPlan && chkMaint && chkMaintStop && chkVat && chkComm && chkDifferent && chkAgree && (!isStagedPlanSelected || chkStaged) && (!isVipLifetimeSelected || chkVip) && (chkInvoiceA && chkInvoiceB)) ? (
                    <button
                      type="submit"
                      className="w-full py-3.5 bg-[#00ffff] hover:bg-[#00ebd4] text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-[0_0_20px_rgba(0,255,255,0.4)] cursor-pointer text-center select-none border-t border-white/20"
                    >
                      Confirm and Register Deal Invoice
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="w-full py-3.5 bg-slate-900 text-slate-500 font-sans font-black text-xs uppercase tracking-widest rounded-xl text-center select-none cursor-not-allowed border border-slate-850"
                    >
                      Confirm and Register Deal Invoice (Locked)
                    </button>
                  )}
                </div>
              </form>
            )}

            {/* TAB CONTAINER 3: CONTACT INFORMATION */}
            {paymentPortalTab === 'CONTACT' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-xs font-mono font-black text-white uppercase tracking-wide">DIRECT FISCAL ENQUIRIES</h4>
                  <p className="text-[10px] text-slate-400 leading-normal pt-1 normal-case font-sans">
                    Should your accounts require bespoke billing routes compliant with custom corporate frameworks, contact our NexoraX administrative line directly.
                  </p>
                </div>

                <div className="p-5 bg-slate-950 border border-slate-800 rounded-2xl space-y-4">
                  <div className="flex items-center gap-3.5">
                    <div className="p-2.5 bg-[#00ffff]/10 rounded-xl border border-[#00ffff]/30">
                      <Phone className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <span className="text-[8px] font-mono text-slate-500 block font-bold">DIRECT FREEPHONE DESK:</span>
                      <strong className="text-white font-sans text-xl tracking-tight select-all">08000 622827</strong>
                      <span className="text-[8px] block font-mono text-emerald-400">● REAL-TIME VOICE RECEPTIVE</span>
                    </div>
                  </div>

                  <hr className="border-slate-900" />

                  <div className="flex items-center gap-3.5">
                    <div className="p-2.5 bg-[#00ffff]/10 rounded-xl border border-[#00ffff]/30">
                      <Mail className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <span className="text-[8px] font-mono text-slate-500 block font-bold">ENTERPRISE SUPPORT CHANNELS:</span>
                      <strong className="text-white font-sans text-sm select-all">support@nexoraxdigital.co.uk</strong>
                      <span className="text-[8px] block font-mono text-cyan-400">Guaranteed Response Speed under 1 Hour</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CHECKOUT SUMMARY PANEL (Right - Col range 5) */}
          <div className="lg:col-span-5 bg-[#050914] border-2 border-[#e840f3]/40 rounded-3xl p-6 space-y-6 text-left shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl pointer-events-none" />
            
            <div className="flex justify-between items-center border-b border-slate-900 pb-3">
              <span className="text-[9.5px] font-mono text-slate-400 uppercase tracking-widest font-black flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-[#e840f3] animate-pulse" />
                Checkout Summary
              </span>
              <span className="px-2 py-0.5 bg-[#e840f3]/25 text-[#e840f3] border border-[#e840f3]/40 font-mono text-[7px] font-black rounded uppercase">
                SECURE
              </span>
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <span className="text-[8px] font-mono text-slate-500 uppercase font-black">SELECTED DEPLOYMENT PLAN</span>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{activePlanObject.emoji}</span>
                    <span className="font-sans font-black text-white uppercase text-base tracking-wide">{activePlanObject.tier} Plan</span>
                  </div>
                  <span className="font-mono text-xs text-cyan-300 font-extrabold">{activePlanObject.priceStr}</span>
                </div>
                <p className="text-[10px] text-slate-400 font-sans normal-case leading-relaxed pt-1.5 border-t border-slate-900/60 mt-1">
                  {activePlanObject.title} • Assigns {activePlanObject.maxAgents} active deployed workers and allows {activePlanObject.maxTasks.toLocaleString()} transaction task runs.
                </p>
              </div>

              {/* Dynamic calculations breakdown */}
              <div className="bg-black/60 rounded-2xl p-4 border border-slate-900 space-y-3 font-mono text-[9px] select-text">
                <div className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider flex justify-between border-b border-white/5 pb-1 mb-1">
                  <span>FISCAL ALLOCATION DECK:</span>
                  <span className="text-emerald-400 font-extrabold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    SECURE_PAYMENT_PROVIDER_CONNECTED
                  </span>
                </div>
                
                <div className="flex justify-between text-slate-400">
                  <span>Selected Plan:</span>
                  <span className="font-bold text-white uppercase">{activePlanObject.tier} ({activePlanObject.title})</span>
                </div>

                <div className="flex justify-between text-slate-400">
                  <span>Billing Cycle:</span>
                  <span className="font-bold text-slate-300">{billingCycle} Cycle</span>
                </div>

                <div className="flex justify-between text-slate-400">
                  <span>Base Price:</span>
                  <span className="font-bold text-white">£{priceInGbp.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>

                {!isDeploymentPlan && (
                  <div className="flex justify-between text-slate-400 items-start">
                    <div className="flex flex-col">
                      <span>10% Maintenance & Hosting Fee:</span>
                      <span className="text-[7.5px] text-slate-500 normal-case font-sans leading-tight">
                        Applies while hosted services remain active, and stops on cancellation.
                      </span>
                    </div>
                    <span className="font-bold text-slate-300">£{maintenanceFeeVal.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                )}

                {isCommunityAllocationComplete ? (
                  <>
                    <div className="flex justify-between text-slate-400">
                      <span>3% Community Allocation Fund:</span>
                      <span className="font-bold text-cyan-400">£{communityAllocation.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>

                    {/* splitting breakdown */}
                    <div className="pl-3 border-l border-cyan-500/20 text-slate-500 text-[8px] space-y-1 py-0.5 bg-black/40 rounded">
                      <div className="flex justify-between">
                        <span>• {adminBeneficiary1} ({adminPercent1.toFixed(2)}%):</span>
                        <span>£{(priceInGbp * (adminPercent1 / 100)).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>• {adminBeneficiary2} ({adminPercent2.toFixed(2)}%):</span>
                        <span>£{(priceInGbp * (adminPercent2 / 100)).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>• {adminBeneficiary3} ({adminPercent3.toFixed(2)}%):</span>
                        <span>£{(priceInGbp * (adminPercent3 / 100)).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                      </div>
                    </div>
                  </>
                ) : isAdminMode ? (
                  <div className="p-2.5 bg-rose-950/20 border-2 border-rose-500/30 rounded-xl text-rose-400 text-[8px] font-mono leading-relaxed uppercase">
                    ❌ Admin Warning: Community Allocation Details Incomplete. Public display is hidden.
                  </div>
                ) : null}

                <div className="flex justify-between text-slate-400">
                  <span>20% Operational Allocation:</span>
                  <span className="font-bold">£{operationalAllocationVal.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>

                <div className="flex justify-between items-start text-slate-500 text-[11px] pt-1 border-t border-slate-900/60">
                  <div className="flex flex-col">
                    <span className="font-bold text-slate-400">UK Standard VAT ({vatApplies ? '20.00%' : '0.00%'}):</span>
                    <span className="text-[8px] text-slate-400 font-mono normal-case tracking-normal pt-0.5">
                      {vatApplies ? (
                        <>
                          VAT: 20% calculated at checkout where applicable.<br />
                          <span className="text-[7.5px] text-slate-500">VAT Number: {backendVatNumberText} | Invoice: VAT invoice available.</span>
                        </>
                      ) : (
                        "VAT: Not charged — NexoraX is not currently VAT-registered."
                      )}
                    </span>
                  </div>
                  <span className="font-mono text-slate-300 font-extrabold">£{vatCalculated.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>

                {backendInternationalTaxWarningEnabled && (
                  <div className="p-2 bg-slate-900/40 border border-slate-800 rounded-xl text-slate-500 text-[7.5px] font-sans normal-case leading-snug">
                    ℹ️ International taxes, VAT, GST, sales tax, or local charges may apply depending on customer location, service type, and payment provider rules. Status: {backendInternationalTaxHandling === 'Manual review' ? 'Subject to manual accountant audit' : backendInternationalTaxHandling}.
                  </div>
                )}

                <hr className="border-white/5" />

                <div className="flex justify-between text-slate-200 text-xs font-black uppercase pt-1 tracking-wider">
                  <span>Total Capital Due today:</span>
                  <span className="text-cyan-300 drop-shadow-[0_0_8px_rgba(0,255,255,0.3)] text-sm">£{grandTotalDue.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>

                <div className="pt-2 text-center border-t border-white/5">
                  <button 
                    type="button"
                    onClick={() => {
                      playNexoraXSound(600, 0.15);
                      setPaymentPortalTab('INVOICE');
                      triggerToast("✔ Switched to secure invoice request panel.", "info");
                    }}
                    className="text-[8px] text-cyan-400 hover:text-cyan-300 underline font-extrabold uppercase tracking-widest cursor-pointer"
                  >
                    Need an invoice? Request Secure Commercial Invoice instead
                  </button>
                </div>
              </div>

              {/* Checkout info metrics */}
              <div className="space-y-1 bg-slate-900/60 p-3 rounded-2xl border border-slate-850 text-[10px] font-mono leading-relaxed uppercase select-text">
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">Billing renewal rate:</span>
                  <span className="text-slate-300 font-extrabold">{billingCycle} Cycle</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">Next renewal trigger:</span>
                  <span className="text-slate-300 font-extrabold">{getRenewalDateStr()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">Routing Transit Route:</span>
                  <span className="text-emerald-400 font-extrabold">Standard SecurePayment direct clearing active</span>
                </div>
              </div>

              {/* SecurePayment checkout button is bright neon cyan (conditionally locked) */}
              {canCheckout ? (
                <>
                  <a 
                    href="https://pay.SecurePayment.co/pay/9fd1aa7e-20b1-44b0-bf57-b75944dd5134"
                    className="block w-full py-3.5 bg-[#00ffff] hover:bg-[#00ebd4] hover:shadow-[0_0_15px_rgba(0,255,255,0.4)] text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl text-center no-underline border border-white/20 transition-all select-none"
                  >
                    Direct SecurePayment Settlement (5% Discount)
                  </a>

                  <a 
                    href="https://buy.stripe.com/eVq9AM0kX8kU1Yz0CWaMU01"
                    className="block w-full py-3 bg-[#e840f3]/20 hover:bg-[#e840f3]/40 border-2 border-[#e840f3] text-[#e840f3] hover:text-white font-sans font-black text-xs uppercase tracking-widest rounded-xl text-center no-underline transition-all select-none"
                  >
                    Standard Card Checkout
                  </a>
                </>
              ) : (
                <div className="space-y-2">
                  <div className="py-3.5 bg-slate-900 text-slate-500 font-sans font-black text-xs uppercase tracking-widest rounded-xl text-center select-none cursor-not-allowed border border-slate-850">
                    SecurePayment Checkout (Locked)
                  </div>
                  <div className="py-3 bg-slate-950 border border-slate-900 text-slate-600 font-sans font-black text-xs uppercase tracking-widest rounded-xl text-center select-none cursor-not-allowed">
                    Stripe Checkout (Locked)
                  </div>
                  <p className="text-[8.5px] font-mono text-center text-rose-400 uppercase tracking-wider animate-pulse pt-1 select-none">
                    ⚠️ Complete Terms Checklist to Unlock Checkout
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------
          SECTION 10 — COMMUNITY ALLOCATION DECK
          ---------------------------------------------------- */}
      {isCommunityAllocationComplete ? (
        <div className="max-w-7xl mx-auto px-6 w-full z-10 text-center space-y-6">
          <div className="bg-[#050914] border border-slate-800 rounded-3xl p-6 text-left relative overflow-hidden shadow-lg animate-fadeIn">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="space-y-1.5 max-w-2xl">
                <h4 className="text-xs font-mono font-black text-[#f574fe] uppercase tracking-widest flex items-center gap-1.5">
                  <Globe className="w-4 h-4 text-pink-400 animate-spin" style={{ animationDuration: '8s' }} />
                  Imperial Community Allocation Protocol
                </h4>
                <p className="text-[10.5px] font-mono text-slate-500 uppercase tracking-wider">
                  3% Base Levy from All Digital Transactions Routed Instantly to Registered Charities
                </p>
                <p className="text-xs text-slate-300 font-sans leading-normal normal-case">
                  Under the direct decree of Director Lead Director, NexoraX permanently binds a 3% base levy onto each membership transaction, completely bypassing standard administrative queues. This fund is distributed across nominated community beneficiaries split equally. Current causes: <strong className="text-white">{adminBeneficiary1} ({adminPercent1}%)</strong>, <strong className="text-white">{adminBeneficiary2} ({adminPercent2}%)</strong>, and <strong className="text-white">{adminBeneficiary3} ({adminPercent3}%)</strong> based on {adminCalcBasis}.
                </p>
              </div>

              <div className="bg-black/60 border border-slate-900 rounded-2xl p-4 shrink-0 font-mono text-[9px] w-full md:w-80">
                <span className="block font-bold text-slate-500 border-b border-slate-900 pb-1.5 mb-2 uppercase">ALLOCATION DISPATCH LOG:</span>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-slate-400">
                    <span>{adminBeneficiary1} ({adminPercent1}%):</span>
                    <span className="text-[#00ffff] font-extrabold">Active</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>{adminBeneficiary2} ({adminPercent2}%):</span>
                    <span className="text-[#00ffff] font-extrabold">Active</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>{adminBeneficiary3} ({adminPercent3}%):</span>
                    <span className="text-[#00ffff] font-extrabold">Active</span>
                  </div>
                  <hr className="border-slate-900" />
                  <div className="text-[#00ffff] font-bold text-[8.5px] uppercase flex justify-between">
                    <span>STATUS DIRECTIVE:</span>
                    <span>100% DISPATCHED</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : isAdminMode ? (
        <div className="max-w-7xl mx-auto px-6 w-full z-10 text-center space-y-3">
          <div className="bg-rose-950/20 border-2 border-rose-500/30 rounded-3xl p-6 text-left relative overflow-hidden shadow-lg animate-pulse">
            <h4 className="text-xs font-mono font-black text-rose-400 uppercase tracking-widest flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-rose-400" />
              Admin Notice: Community Allocation Details Required
            </h4>
            <p className="text-xs text-slate-400 font-sans leading-normal normal-case pt-1.5">
              The public 3% community allocation disclosure and donation statistics block is currently hidden from public visitors. To unlock this module on the frontend, please ensure all nominated beneficiary names, split ratios, Cap structures, and tax basis parameters are fully populated in the Admin Panel terms editor.
            </p>
          </div>
        </div>
      ) : null}

      {/* ----------------------------------------------------
          SECTION 11 — CLIENT-FACING BILLING DASHBOARD
          ---------------------------------------------------- */}
      <div className="max-w-7xl mx-auto px-6 w-full space-y-6">
        <div className="flex flex-col items-center text-center space-y-1">
          <h3 className="text-sm font-black font-mono text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-cyan-400 animate-pulse" />
            Client Billings & Metering Dashboard
          </h3>
          <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
            Real-time query tracking and transaction history anchors
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* METERING PANEL */}
          <div className="bg-[#050914] border-2 border-slate-800 rounded-3xl p-6 text-left space-y-4">
            <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider flex items-center gap-1.5">
              <Cpu className="w-4 h-4 text-cyan-400" />
              Cognitive Query Meter
            </h4>

            <div className="space-y-2 pt-2">
              <div className="flex justify-between text-[11px] font-mono">
                <span className="text-slate-400 uppercase">MONTHLY CORE QUERIES:</span>
                <strong className="text-cyan-300">{queriesLeft.toLocaleString()} / {queriesMax.toLocaleString()} Used</strong>
              </div>

              {/* Visual meter bar */}
              <div className="w-full bg-slate-905 h-2.5 rounded-full border border-slate-800 overflow-hidden relative">
                <div 
                  className="bg-gradient-to-r from-cyan-400 to-[#e840f3] h-full transition-all duration-1000"
                  style={{ width: `${(queriesLeft / queriesMax) * 100}%` }}
                />
              </div>

              <span className="block text-[8px] font-mono text-slate-500 uppercase tracking-widest pt-1 leading-normal">
                Query threshold loops back automatically on your monthly billing date.
              </span>
            </div>

            <div className={`pt-4 border-t border-slate-950 grid ${isAdminMode ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
              <button 
                onClick={() => {
                  playNexoraXSound(900, 0.15);
                  setQueriesMax(prev => prev + 5000);
                  triggerToast("✔ Added +5,000 queries block onto your active volume parameters!", "success");
                }}
                className="py-2 bg-slate-900 hover:bg-slate-850 border border-slate-800 text-slate-300 text-[9px] font-mono font-bold uppercase rounded-lg text-center cursor-pointer transition-colors"
              >
                +5k queries block
              </button>
              {isAdminMode && (
                <button 
                  onClick={() => {
                    playNexoraXSound(1000, 0.15);
                    triggerToast("★ Opening dedicated Principal Private Secretary (Secure Invoice Register) interview registry...", "info");
                  }}
                  className="py-2 bg-gradient-to-r from-cyan-400/20 to-purple-600/20 text-cyan-300 border border-cyan-500/40 text-[9px] font-mono font-black uppercase rounded-lg text-center cursor-pointer transition-colors"
                >
                  Secure Invoice Register Placement Desk
                </button>
              )}
            </div>
          </div>

          {/* ACTIVE ACCOUNT METRICS */}
          <div className="bg-[#050914] border-2 border-slate-800 rounded-3xl p-6 text-left space-y-4">
            <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-cyan-400" />
              Account Status parameters
            </h4>

            <div className="space-y-2 pt-2 text-[10.5px] font-mono uppercase leading-relaxed text-slate-300">
              <p className="flex justify-between border-b border-slate-900 pb-1.5">
                <span className="text-slate-500">Subscription level:</span>
                <span className="text-[#e840f3] font-extrabold">{activeClientPlan} Core Tier</span>
              </p>
              <p className="flex justify-between border-b border-slate-900 pb-1.5">
                <span className="text-slate-500">Billing Cycle:</span>
                <span className="text-slate-300 font-bold">{clientCycle}</span>
              </p>
              <p className="flex justify-between border-b border-slate-900 pb-1.5">
                <span className="text-slate-500">Security Gate status:</span>
                <span className="text-emerald-400 font-extrabold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                  Active (Secure)
                </span>
              </p>
              <p className="flex justify-between">
                <span className="text-slate-500">Linked Account:</span>
                <span className="text-white select-all">siddique.mahmood@gmail.com</span>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <button 
                onClick={() => {
                  playNexoraXSound(1100, 0.2);
                  triggerToast("📋 PDF Statement compiled & downloaded to Google Drive NexoraX Resource Library!", "success");
                }}
                className="py-2 bg-slate-900 hover:bg-slate-850 border border-slate-800 text-slate-300 text-[9px] font-mono font-bold uppercase rounded-lg text-center cursor-pointer transition-colors flex items-center justify-center gap-1"
              >
                <Download size={10} />
                <span>PDF statement</span>
              </button>
              <button 
                onClick={() => {
                  playNexoraXSound(1200, 0.2);
                  triggerToast("★ Opening advisory calendar. Slots verified with Staff Coordinator.", "success");
                }}
                className="py-2 bg-slate-900 hover:bg-slate-850 border border-slate-800 text-slate-300 text-[9px] font-mono font-bold uppercase rounded-lg text-center cursor-pointer transition-colors"
              >
                Book Advisory Slot
              </button>
            </div>
          </div>

          {/* HISTORICAL LEDGER */}
          <div className="bg-[#050914] border-2 border-slate-800 rounded-3xl p-6 text-left space-y-4">
            <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider flex items-center gap-1.5">
              <Landmark className="w-4 h-4 text-cyan-400" />
              NexoraX Ledger Statements
            </h4>

            <div className="space-y-2 pt-2">
              {simulatedInvoices.map((inv) => (
                <div key={inv.id} className="flex justify-between items-center text-[10px] font-mono bg-black/45 hover:bg-black/80 border border-slate-900 p-2 rounded-xl transition-colors">
                  <div className="text-left space-y-0.5">
                    <span className="text-white font-extrabold block truncate pr-2">{inv.id}</span>
                    <span className="text-[8.5px] text-slate-500 block uppercase font-bold">{inv.date}</span>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <span className="text-cyan-300 font-extrabold">{inv.amount}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[7.5px] font-black ${inv.status === 'PAID' ? 'bg-emerald-950 text-emerald-400 border border-emerald-500/25' : 'bg-amber-950 text-amber-400 border border-amber-500/25 animate-pulse'}`}>
                      {inv.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------
          SECTION 12 — ADMIN-ONLY PRICING MANAGER
          ---------------------------------------------------- */}
      <div className="max-w-7xl mx-auto px-6 w-full z-10 text-center">
        
        {/* Toggle Admin Telemetry explicitly */}
        <div className="inline-flex items-center gap-2.5 bg-slate-950 border border-slate-800 px-4 py-2 rounded-xl text-slate-500 hover:text-slate-300 cursor-pointer transition-colors mb-6 text-xs font-mono font-bold">
          <input 
            type="checkbox"
            id="admin-telemetry-panel"
            checked={isAdminMode}
            onChange={async (e) => {
              playNexoraXSound(e.target.checked ? 880 : 440, 0.2);
              if (e.target.checked) {
                await verifyAdminPricingAccess();
              } else {
                setIsAdminMode(false);
                triggerToast("Admin Dashboard view hidden.", "info");
              }
            }}
          />
          <label htmlFor="admin-telemetry-panel" className="cursor-pointer select-none">
            ENABLE ADMIN PRICING PANELS & TOOLS (SC887095)
          </label>
        </div>

        {isAdminMode && (
          <div className="border border-rose-500/30 rounded-[2.5rem] bg-black/95 p-6 md:p-10 text-left space-y-8 shadow-[0_0_50px_rgba(239,68,68,0.15)] relative overflow-hidden animate-fadeIn">
            <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 rounded-full blur-2xl pointer-events-none" />
            
            <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-rose-900/60 pb-5 gap-4">
              <div className="space-y-1">
                <span className="text-[10px] uppercase font-mono text-rose-500 tracking-wider font-extrabold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded bg-rose-500 animate-ping"></span>
                  Node-17 (Chronos Ledger) Active
                </span>
                <h3 className="text-2xl md:text-3xl font-sans font-black text-white uppercase tracking-tight">Admin Pricing & Tiers Manager</h3>
                <p className="text-[10.5px] font-mono text-slate-400 normal-case">
                  Configure corporate pricing limits, adjust base levels, and compile outreach schedules instantly.
                </p>
              </div>

              <div className="bg-rose-950/20 border border-rose-500/30 p-3 rounded-2xl">
                <span className="text-[7.5px] font-mono text-rose-400 font-extrabold block uppercase">Total Gross Harvest (SC887095):</span>
                <span className="text-xl font-mono font-black text-rose-200">£2,820,000.00</span>
              </div>
            </div>

            {/* FORM GRID: EDIT BASE PRICES & LIMITS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              <div className="space-y-4 bg-slate-950 p-5 rounded-2xl border border-slate-900">
                <h4 className="text-xs font-mono font-black text-rose-300 uppercase tracking-wide flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5 text-rose-400" />
                  Edit Tier Base Prices
                </h4>

                <div className="space-y-3.5 pt-2">
                  {[
                    { id: 'bronze', label: 'Bronze Plan (£)' },
                    { id: 'silver', label: 'Silver Plan (£)' },
                    { id: 'gold', label: 'Gold Plan (£)' },
                    { id: 'diamond', label: 'Diamond Plan (£)' },
                    { id: 'platinum', label: 'Platinum Plan (£)' },
                    { id: 'black_diamond', label: 'Black Diamond Plan (£)' }
                  ].map((p) => (
                    <div key={p.id} className="flex justify-between items-center gap-2">
                      <span className="text-[9.5px] font-mono text-slate-400 font-bold uppercase">{p.label}</span>
                      <input 
                        type="number"
                        value={adminPrices[p.id]}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setAdminPrices({ ...adminPrices, [p.id]: val });
                        }}
                        className="w-24 bg-black border border-slate-800 rounded px-2 py-1 text-xs font-mono text-white focus:border-rose-500 outline-none text-right"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4 bg-slate-950 p-5 rounded-2xl border border-slate-900">
                <h4 className="text-xs font-mono font-black text-rose-300 uppercase tracking-wide flex items-center gap-1.5">
                  <Cpu className="w-3.5 h-3.5 text-rose-400" />
                  Edit Plan Capacity Limits
                </h4>

                <div className="space-y-3.5 pt-2">
                  {[
                    { id: 'bronze', name: 'BRONZE' },
                    { id: 'silver', name: 'SILVER' },
                    { id: 'gold', name: 'GOLD' },
                    { id: 'diamond', name: 'DIAMOND' },
                    { id: 'platinum', name: 'PLATINUM' },
                    { id: 'black_diamond', name: 'BLACK DIAMOND' }
                  ].map((p) => (
                    <div key={p.id} className="flex flex-col gap-1.5 border-b border-slate-900 pb-2.5 last:border-0 last:pb-0">
                      <span className="text-[10px] font-mono text-slate-400 font-black">{p.name} PLAN:</span>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex justify-between items-center gap-1">
                          <span className="text-[8px] text-slate-500 font-bold">AGENTS:</span>
                          <input 
                            type="number"
                            value={adminLimits[p.id].maxAgents}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setAdminLimits({
                                ...adminLimits,
                                [p.id]: { ...adminLimits[p.id], maxAgents: v }
                              });
                            }}
                            className="w-16 bg-black border border-slate-800 rounded px-1.5 py-0.5 text-[9.5px] font-mono text-white focus:border-rose-500 outline-none text-center"
                          />
                        </div>
                        <div className="flex justify-between items-center gap-1">
                          <span className="text-[8px] text-slate-500 font-bold">TASKS:</span>
                          <input 
                            type="number"
                            value={adminLimits[p.id].maxTasks}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setAdminLimits({
                                ...adminLimits,
                                [p.id]: { ...adminLimits[p.id], maxTasks: v }
                              });
                            }}
                            className="w-16 bg-black border border-slate-800 rounded px-1.5 py-0.5 text-[9.5px] font-mono text-white focus:border-rose-500 outline-none text-center"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* OUTREACH GENERATOR SECTION */}
              <div className="space-y-4 bg-slate-950 p-5 rounded-2xl border border-rose-500/25">
                <h4 className="text-xs font-mono font-black text-rose-300 uppercase tracking-wide flex items-center gap-1.5">
                  <Copy className="w-3.5 h-3.5 text-rose-400" />
                  NexoraX Outreach Generator
                </h4>

                <div className="space-y-3.5 pt-2 text-[10.5px]">
                  <div className="space-y-1">
                    <span className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">1. Candidate Full Name</span>
                    <input 
                      type="text"
                      value={candidateName}
                      onChange={(e) => setCandidateName(e.target.value)}
                      placeholder="e.g. Eleanor Vance"
                      className="w-full bg-black border border-slate-800 rounded px-3 py-1.5 font-mono text-xs text-white placeholder-slate-600 focus:border-rose-500 outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <span className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">2. Placement Agency</span>
                    <input 
                      type="text"
                      value={targetAgency}
                      onChange={(e) => setTargetAgency(e.target.value)}
                      placeholder="e.g. Executive Placements Group"
                      className="w-full bg-black border border-slate-800 rounded px-3 py-1.5 font-mono text-xs text-white placeholder-slate-600 focus:border-rose-500 outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <span className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">3. Assigned Client Tier Node</span>
                    <select 
                      value={outreachTier}
                      onChange={(e) => setOutreachTier(e.target.value)}
                      className="w-full bg-black border border-slate-800 rounded px-2.5 py-1.5 font-mono text-xs text-rose-300 focus:border-rose-500 outline-none cursor-pointer"
                    >
                      <option value="Bronze Block">Bronze Block (£499)</option>
                      <option value="Silver Block">Silver Block (£999)</option>
                      <option value="Gold Block">Gold Block (£2,499)</option>
                      <option value="Diamond VIP Deck">Diamond VIP Deck (£4,999)</option>
                    </select>
                  </div>

                  <button
                    onClick={handleCopyScript}
                    disabled={!candidateName || !targetAgency}
                    className="w-full py-2 bg-gradient-to-r from-red-650 to-red-800 hover:bg-red-700 hover:shadow-[0_0_12px_rgba(239,68,68,0.4)] disabled:opacity-40 disabled:hover:shadow-none text-white font-mono text-[9px] font-black uppercase tracking-widest rounded-lg cursor-pointer transition-all"
                  >
                    Generate Outreach Script
                  </button>
                </div>
              </div>
            </div>

            {/* ADMIN-ONLY TERMS, DISCLOSURES & ROUTING CONFIGURATOR */}
            <div className="bg-slate-950 p-6 rounded-3xl border border-rose-500/25 space-y-4">
              <div className="flex items-center gap-2 border-b border-rose-900/60 pb-2">
                <ShieldCheck className="w-5 h-5 text-rose-400" />
                <h4 className="text-xs font-mono font-black text-[#f574fe] uppercase tracking-widest">Admin Terms, Disclosures & Routing Policies Manager</h4>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left config column: links and cancellation routing */}
                <div className="space-y-3.5 text-[10px]">
                  <h5 className="font-mono text-[9px] font-black text-rose-300 uppercase tracking-widest border-b border-white/5 pb-1">Legal Document Hyperlinks</h5>
                  
                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">Terms of Service URL</label>
                    <input 
                      type="text"
                      className="w-full bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                      value={adminTermsOfServiceLink}
                      onChange={(e) => setAdminTermsOfServiceLink(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">Privacy Policy URL</label>
                    <input 
                      type="text"
                      className="w-full bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                      value={adminPrivacyPolicyLink}
                      onChange={(e) => setAdminPrivacyPolicyLink(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">Refund Policy URL</label>
                    <input 
                      type="text"
                      className="w-full bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                      value={adminRefundPolicyLink}
                      onChange={(e) => setAdminRefundPolicyLink(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">Cancellation Policy URL</label>
                    <input 
                      type="text"
                      className="w-full bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                      value={adminCancellationPolicyLink}
                      onChange={(e) => setAdminCancellationPolicyLink(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">Cancellation Request Email/Routing Target</label>
                    <input 
                      type="text"
                      className="w-full bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-xs text-rose-300 focus:border-rose-500 outline-none"
                      value={adminCancellationRouting}
                      onChange={(e) => setAdminCancellationRouting(e.target.value)}
                    />
                  </div>

                  {/* UK & INTERNATIONAL VAT CONFIGURATION MANAGER */}
                  <div className="space-y-3 mt-4 pt-4 border-t border-rose-950/40 select-none">
                    <h5 className="font-mono text-[9px] font-black text-[#e840f3] uppercase tracking-widest border-b border-white/5 pb-1 flex items-center gap-1">
                      <Shield className="w-3.5 h-3.5 text-pink-400" />
                      UK & International Tax Control Node
                    </h5>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                      <div className="space-y-1">
                        <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase block">VAT Registration Status</label>
                        <select 
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                          value={adminVatStatus}
                          onChange={(e) => setAdminVatStatus(e.target.value)}
                        >
                          <option value="Not registered">Not registered</option>
                          <option value="Registered">Registered</option>
                          <option value="Pending registration">Pending registration</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase block font-medium">VAT Number</label>
                        <input 
                          type="text"
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                          value={adminVatNumber}
                          onChange={(e) => setAdminVatNumber(e.target.value)}
                          placeholder="GB982310622"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                      <div className="space-y-1">
                        <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase block">VAT Effective Date</label>
                        <input 
                          type="date"
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                          value={adminVatEffectiveDate}
                          onChange={(e) => setAdminVatEffectiveDate(e.target.value)}
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase block">Voluntary Registration</label>
                        <select 
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                          value={adminVoluntaryRegistration ? "true" : "false"}
                          onChange={(e) => setAdminVoluntaryRegistration(e.target.value === "true")}
                        >
                          <option value="true">Voluntary Registration Enabled (Yes)</option>
                          <option value="false">Voluntary Registration Disabled (No)</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                      <div className="space-y-1">
                        <div className="flex justify-between items-center text-[8px] font-mono text-slate-400 font-extrabold uppercase">
                          <label>Rolling 12-Month Turnover</label>
                          <span className="text-cyan-400 font-black">Limit: £90k</span>
                        </div>
                        <input 
                          type="number"
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                          value={adminRollingTurnover}
                          onChange={(e) => setAdminRollingTurnover(Number(e.target.value))}
                          placeholder="e.g. 125000"
                        />
                      </div>

                      <div className="space-y-1">
                        <div className="flex justify-between items-center text-[8px] font-mono text-slate-400 font-extrabold uppercase">
                          <label>Next 30-Day expected</label>
                          <span className="text-cyan-300 font-black">Trigger: £90k</span>
                        </div>
                        <input 
                          type="number"
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                          value={adminSingleMonthExpectedTurnover}
                          onChange={(e) => setAdminSingleMonthExpectedTurnover(Number(e.target.value))}
                          placeholder="e.g. 15000"
                        />
                      </div>
                    </div>

                    <div className="space-y-1 mb-2">
                      <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase block font-medium">VAT Display Description Wording</label>
                      <textarea 
                        className="w-full h-11 bg-black border border-slate-800 rounded-lg px-2.5 py-1 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                        value={adminVatDisplayWordingState}
                        onChange={(e) => setAdminVatDisplayWordingState(e.target.value)}
                        placeholder="Customize VAT checkout label..."
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                      <div className="space-y-1">
                        <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase block">VAT Invoice Enabled</label>
                        <select 
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                          value={adminVatInvoiceEnabled ? "true" : "false"}
                          onChange={(e) => setAdminVatInvoiceEnabled(e.target.value === "true")}
                        >
                          <option value="true">Invoice Enabled (Yes)</option>
                          <option value="false">Invoice Disabled (No)</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase block">International Warning</label>
                        <select 
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                          value={adminInternationalTaxWarningEnabled ? "true" : "false"}
                          onChange={(e) => setAdminInternationalTaxWarningEnabled(e.target.value === "true")}
                        >
                          <option value="true">Warning Displayed (Yes)</option>
                          <option value="false">Warning Hidden (No)</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                      <div className="space-y-1">
                        <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase block">Accountant Review Status</label>
                        <select 
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                          value={adminAccountantReviewStatus}
                          onChange={(e) => setAdminAccountantReviewStatus(e.target.value)}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Reviewed">Reviewed</option>
                          <option value="Approved">Approved</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase block">International Tax Handling</label>
                        <select 
                          className="w-full bg-black border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-[11px] text-white focus:border-rose-500 outline-none"
                          value={adminInternationalTaxHandling}
                          onChange={(e) => setAdminInternationalTaxHandling(e.target.value)}
                        >
                          <option value="Manual review">Manual review</option>
                          <option value="Payment provider tax calculation">Payment provider tax calculation</option>
                          <option value="External accountant review required">External accountant review required</option>
                        </select>
                      </div>
                    </div>

                    <div className="p-2.5 bg-slate-900/60 rounded-xl border border-slate-850 text-[8.5px] font-mono leading-relaxed normal-case">
                      <div className="flex justify-between items-center font-bold">
                        <span className="text-slate-500 uppercase text-[8px]">LOCAL THRESHOLD RESOLUTION:</span>
                        {vatApplies ? (
                          <span className="text-[#00ffff] uppercase font-black text-[8px] tracking-wider animate-pulse flex items-center gap-1">
                            <span className="w-1 h-1 rounded-full bg-[#00ffff] animate-ping" />
                            Active Tax collection (20.00% VAT)
                          </span>
                        ) : (
                          <span className="text-rose-400 uppercase font-black text-[8px]">Non-registered status / Turnover low</span>
                        )}
                      </div>
                      <p className="text-slate-400 mt-1">
                        {vatApplies 
                          ? "We are currently collecting VAT. Your rolling taxable turnover of £" + adminRollingTurnover.toLocaleString() + " or next 30-day expectance of £" + adminSingleMonthExpectedTurnover.toLocaleString() + " meets/exceeds the £90,000 UK registration threshold rules." 
                          : "VAT is not levied. Turnover resides below the standard UK £90,000 mandatory tax threshold."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right config column: checkbox wording overrides */}
                <div className="space-y-3.5 text-[10px]">
                  <h5 className="font-mono text-[9px] font-black text-rose-300 uppercase tracking-widest border-b border-white/5 pb-1">Dynamic Checkbox Wording Configuration</h5>

                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">1. Maintenance & Hosting Fee Disclosure</label>
                    <textarea 
                      className="w-full h-14 bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                      value={adminMaintenanceFeeWording}
                      onChange={(e) => setAdminMaintenanceFeeWording(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">2. 3% Community Allocation Fund Wording</label>
                    <textarea 
                      className="w-full h-14 bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                      value={adminCommunityAllocationWording}
                      onChange={(e) => setAdminCommunityAllocationWording(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">3. VAT rate & liability wording</label>
                    <textarea 
                      className="w-full h-14 bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                      value={adminVatWording}
                      onChange={(e) => setAdminVatWording(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">4. Approved Staged Payment Plan terms</label>
                    <textarea 
                      className="w-full h-14 bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                      value={adminStagedWording}
                      onChange={(e) => setAdminStagedWording(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">5. VIP Lifetime access criteria</label>
                    <textarea 
                      className="w-full h-14 bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                      value={adminVipWording}
                      onChange={(e) => setAdminVipWording(e.target.value)}
                    />
                  </div>

                  {/* SUBSECTION: NEXORAX REVENUE & POLICIES TERM MANAGER FIELD CONTROLS */}
                  <div className="space-y-1 pt-3 border-t border-rose-950/40 mt-3">
                    <h5 className="font-mono text-[9px] font-black text-rose-300 uppercase tracking-widest border-b border-white/5 pb-1">NexoraX Policies & Invoice Terms</h5>
                  </div>

                  <div className="space-y-1 mt-3">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">General Terms Acceptance Prefix Text</label>
                    <input 
                      type="text"
                      className="w-full bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-xs text-white focus:border-rose-500 outline-none"
                      value={adminGeneralTermsWording}
                      onChange={(e) => setAdminGeneralTermsWording(e.target.value)}
                      placeholder="e.g. I agree to the NexoraX"
                    />
                  </div>

                  <div className="space-y-1 mt-3">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">Invoice Request Terms (Line 1)</label>
                    <textarea 
                      className="w-full h-14 bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                      value={adminInvoiceWordingA}
                      onChange={(e) => setAdminInvoiceWordingA(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1 mt-3">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">Invoice Request Terms (Line 2)</label>
                    <textarea 
                      className="w-full h-14 bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                      value={adminInvoiceWordingB}
                      onChange={(e) => setAdminInvoiceWordingB(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1 mt-3">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">Refund Policy Card Brief Summary text</label>
                    <textarea 
                      className="w-full h-14 bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                      value={adminRefundPolicySummary}
                      onChange={(e) => setAdminRefundPolicySummary(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1 mt-3">
                    <label className="text-[8px] font-mono text-slate-400 font-extrabold uppercase">Cancellation Policy Card Brief Summary text</label>
                    <textarea 
                      className="w-full h-14 bg-black border border-slate-800 rounded-lg px-3 py-1.5 font-mono text-[10.5px] text-white focus:border-rose-500 outline-none resize-none"
                      value={adminCancellationPolicySummary}
                      onChange={(e) => setAdminCancellationPolicySummary(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Master Synchronize Command desk */}
              <div className="p-4 bg-rose-950/20 border border-rose-500/25 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 mt-4 text-left">
                <div className="space-y-1">
                  <span className="font-mono text-[9px] font-black text-[#f574fe] uppercase tracking-widest block">NexoraX Term Verification Desk</span>
                  <p className="text-[9px] text-slate-400 leading-normal normal-case">
                    Commit modified text parameters permanently to update checkouts and modals dynamically across target instances.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      localStorage.setItem('nx_adminTermsOfServiceLink', adminTermsOfServiceLink);
                      localStorage.setItem('nx_adminPrivacyPolicyLink', adminPrivacyPolicyLink);
                      localStorage.setItem('nx_adminRefundPolicyLink', adminRefundPolicyLink);
                      localStorage.setItem('nx_adminCancellationPolicyLink', adminCancellationPolicyLink);
                      localStorage.setItem('nx_adminCancellationRouting', adminCancellationRouting);
                      localStorage.setItem('adminRollingTurnover', String(adminRollingTurnover));
                      localStorage.setItem('adminSingleMonthExpectedTurnover', String(adminSingleMonthExpectedTurnover));
                      
                      localStorage.setItem('nx_adminMaintenanceFeeWording', adminMaintenanceFeeWording);
                      localStorage.setItem('nx_adminCommunityAllocationWording', adminCommunityAllocationWording);
                      localStorage.setItem('nx_adminVatWording', adminVatWording);
                      localStorage.setItem('nx_adminStagedWording', adminStagedWording);
                      localStorage.setItem('nx_adminVipWording', adminVipWording);

                      localStorage.setItem('nx_adminInvoiceWordingA', adminInvoiceWordingA);
                      localStorage.setItem('nx_adminInvoiceWordingB', adminInvoiceWordingB);
                      localStorage.setItem('nx_adminRefundPolicySummary', adminRefundPolicySummary);
                      localStorage.setItem('nx_adminCancellationPolicySummary', adminCancellationPolicySummary);
                      localStorage.setItem('nx_adminGeneralTermsWording', adminGeneralTermsWording);

                      // Save our advanced VAT/Tax configuration fields
                      localStorage.setItem('nx_adminVatStatus', adminVatStatus);
                      localStorage.setItem('nx_adminVatNumber', adminVatNumber);
                      localStorage.setItem('nx_adminVatEffectiveDate', adminVatEffectiveDate);
                      localStorage.setItem('nx_adminVoluntaryRegistration', adminVoluntaryRegistration ? "true" : "false");
                      localStorage.setItem('nx_adminVatDisplayWording', adminVatDisplayWordingState);
                      localStorage.setItem('nx_adminVatInvoiceEnabled', adminVatInvoiceEnabled ? "true" : "false");
                      localStorage.setItem('nx_adminInternationalTaxWarningEnabled', adminInternationalTaxWarningEnabled ? "true" : "false");
                      localStorage.setItem('nx_adminAccountantReviewStatus', adminAccountantReviewStatus);
                      localStorage.setItem('nx_adminInternationalTaxHandling', adminInternationalTaxHandling);

                      // Commit of Server Config State via API update-config
                      const sendUpdateToken = async () => {
                        try {
                          const token = auth.currentUser ? await auth.currentUser.getIdToken() : '';
                          const headers: Record<string, string> = {
                            'Content-Type': 'application/json'
                          };
                          if (token) {
                            headers['Authorization'] = `Bearer ${token}`;
                          }
                          const res = await fetch('/api/billing/update-config', {
                            method: 'POST',
                            headers,
                            body: JSON.stringify({
                              vatStatus: adminVatStatus,
                              vatNumber: adminVatNumber,
                              vatEffectiveDate: adminVatEffectiveDate,
                              rollingTurnover: adminRollingTurnover,
                              expected30DayTurnover: adminSingleMonthExpectedTurnover,
                              voluntaryRegistration: adminVoluntaryRegistration,
                              vatDisplayWording: adminVatDisplayWordingState,
                              vatInvoiceEnabled: adminVatInvoiceEnabled,
                              internationalTaxWarningEnabled: adminInternationalTaxWarningEnabled,
                              accountantReviewStatus: adminAccountantReviewStatus,
                              internationalTaxHandling: adminInternationalTaxHandling
                            })
                          });
                          const data = await res.json();
                          if (data.status === 'success') {
                            setBackendBillingConfig(data.billingConfig);
                            setBackendVatApplies(data.vatApplies);
                            setBackendVatRate(data.vatRate);
                            setBackendVatAmount(data.calculation?.vatAmount || 0);
                          }
                        } catch (err) {
                          console.error("Could not synchronize dynamic server tax database:", err);
                        }
                      };
                      sendUpdateToken();

                      // Dispatch standard storage event to sync with other active templates
                      window.dispatchEvent(new Event('storage'));
                      
                      playNexoraXSound(880, 0.35);
                      triggerToast("★ NexoraX Policy, Terms and Server-side VAT database successfully committed and synced.", "success");
                    } catch (err) {
                      triggerToast("Failed to write to local storage memory.", "error");
                    }
                  }}
                  className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 hover:shadow-[0_0_15px_rgba(16,185,129,0.3)] text-white font-mono text-[9px] font-black uppercase tracking-widest rounded-xl transition-all cursor-pointer whitespace-nowrap"
                >
                  Save All System Terms
                </button>
              </div>

              <div className="pt-2.5 border-t border-slate-900 flex justify-between items-center text-[8.5px] font-mono text-slate-500 uppercase select-none">
                <span>Terms Manager Protocol Node Active (18.5)</span>
                <span className="text-emerald-400 font-bold">● COGNITIVE_MANDATE_VERIFIED</span>
              </div>
            </div>

            {/* SHOW GENERATED OUTPUT SCRIPT BOX */}
            {candidateName && targetAgency && (
              <div className="space-y-2 pt-2 border-t border-slate-900">
                <div className="flex justify-between items-center text-[9px] font-mono text-slate-500">
                  <span>OUTFLOW MANDATE PREVIEW:</span>
                  <button onClick={handleCopyScript} className="text-[#00ffff] font-extrabold hover:underline">
                    COPY FULL PLAINTEXT
                  </button>
                </div>
                <pre className="w-full h-48 bg-black/80 border border-slate-900 rounded-2xl p-4 overflow-y-auto font-mono text-[9px] leading-relaxed text-slate-300 select-all whitespace-pre-wrap text-left">
                  {getOutreachScript()}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Driver/Courier/Fleet Systems Quote Form */}
        <div 
          id="driver-fleet-quote" 
          className="max-w-4xl mx-auto px-6 w-full py-12 scroll-mt-24"
        >
          <div className="bg-gradient-to-br from-[#0b0f19]/90 via-[#03060f]/98 to-orange-950/10 border-2 border-orange-500/20 rounded-[2.5rem] p-8 md:p-10 space-y-6 relative shadow-2xl hover:border-orange-500/40 hover:shadow-[0_0_50px_rgba(249,115,22,0.15)] transition-all duration-500">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-orange-500 to-amber-400 rounded-t-2xl" />
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b border-white/5 pb-6">
              <div>
                <span className="text-[9px] bg-orange-950/40 text-orange-400 font-mono font-black uppercase tracking-widest border border-orange-500/30 px-3 py-1 rounded-full inline-block mb-2">
                  🚚 B2B Logistics Dispatch Matrix
                </span>
                <h3 className="text-xl md:text-2xl font-sans font-black uppercase text-white tracking-tight">
                  Request a Driver / Courier / Fleet System Quote
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed normal-case mt-2 max-w-2xl font-medium">
                  Tell us about your courier, delivery, taxi, logistics, field-service, or mobile-worker operation and NexoraX Digital will prepare a custom proposal.
                </p>
              </div>
              
              <div className="bg-orange-950/10 border border-orange-500/20 p-4 rounded-xl max-w-xs text-left shrink-0">
                <span className="text-orange-400 font-mono text-[9px] font-black uppercase tracking-wider block">🤝 DIRECT SYSTEM ARCHITECT REVIEW</span>
                <p className="text-[9.5px] text-slate-400 normal-case leading-relaxed font-sans mt-1">Senior planning engineers assess all fleet/driver system parameters within 24 hours. Quotes are prepared with detailed phase mapping.</p>
              </div>
            </div>

            {driverQuoteSuccess ? (
              <div className="flex flex-col items-center text-center p-8 bg-emerald-950/20 border border-emerald-500/30 rounded-3xl space-y-4">
                <div className="w-16 h-16 bg-emerald-500/10 text-emerald-400 rounded-full border border-emerald-500/30 flex items-center justify-center text-3xl animate-bounce">
                  ✓
                </div>
                <h4 className="text-lg font-sans font-black text-white uppercase tracking-wider">Custom proposal request received!</h4>
                <p className="text-xs text-slate-350 leading-relaxed font-sans max-w-md normal-case">
                  Your request has been registered under &ldquo;Driver / Courier / Fleet Systems&rdquo;. Our Senior Systems Architect will review your requirements and prepare a custom proposal.
                </p>
                <button 
                  onClick={() => setDriverQuoteSuccess(false)}
                  className="mt-4 px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmitDriverQuote} className="grid grid-cols-1 md:grid-cols-2 gap-5 text-left font-mono">
                <div className="space-y-1.5">
                  <label className="text-orange-400 uppercase font-black text-[9px] tracking-wider block">Your Name *</label>
                  <input 
                    type="text"
                    required
                    disabled={driverQuoteSubmitting}
                    value={driverQuoteName}
                    onChange={(e) => setDriverQuoteName(e.target.value)}
                    placeholder="e.g. Siddique Mahmood"
                    className="w-full bg-black/60 border border-zinc-900 rounded-xl p-3 text-white text-xs focus:border-orange-500 focus:outline-none transition-all disabled:opacity-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-orange-400 uppercase font-black text-[9px] tracking-wider block">Business Name</label>
                  <input 
                    type="text"
                    disabled={driverQuoteSubmitting}
                    value={driverQuoteBusiness}
                    onChange={(e) => setDriverQuoteBusiness(e.target.value)}
                    placeholder="e.g. Apex Courier Logistics"
                    className="w-full bg-black/60 border border-zinc-900 rounded-xl p-3 text-white text-xs focus:border-orange-500 focus:outline-none transition-all disabled:opacity-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-orange-400 uppercase font-black text-[9px] tracking-wider block">Email Address *</label>
                  <input 
                    type="email"
                    required
                    disabled={driverQuoteSubmitting}
                    value={driverQuoteEmail}
                    onChange={(e) => setDriverQuoteEmail(e.target.value)}
                    placeholder="e.g. lead.director@nexorax.co.uk"
                    className="w-full bg-black/60 border border-zinc-900 rounded-xl p-3 text-white text-xs focus:border-orange-500 focus:outline-none transition-all disabled:opacity-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-orange-400 uppercase font-black text-[9px] tracking-wider block">Phone Number</label>
                  <input 
                    type="tel"
                    disabled={driverQuoteSubmitting}
                    value={driverQuotePhone}
                    onChange={(e) => setDriverQuotePhone(e.target.value)}
                    placeholder="e.g. +44 7359 622827"
                    className="w-full bg-black/60 border border-zinc-900 rounded-xl p-3 text-white text-xs focus:border-orange-500 focus:outline-none transition-all disabled:opacity-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-orange-400 uppercase font-black text-[9px] tracking-wider block">Business Type</label>
                  <select
                    disabled={driverQuoteSubmitting}
                    value={driverQuoteBusinessType}
                    onChange={(e) => setDriverQuoteBusinessType(e.target.value)}
                    className="w-full bg-black/60 border border-zinc-900 rounded-xl p-3 text-white text-xs focus:border-orange-500 focus:outline-none transition-all cursor-pointer disabled:opacity-50"
                  >
                    <option value="Courier">Courier / Same-Day Delivery</option>
                    <option value="Taxi">Taxi / Private Hire / Ride-Hailing</option>
                    <option value="Field-Service">Field-Service / Mobile Maintenance</option>
                    <option value="Fleet Logistics">Fleet Logistics / Distribution</option>
                    <option value="Food Delivery">Food / Restaurant Delivery Fleet</option>
                    <option value="Other">Other Mobile-Worker System</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-orange-400 uppercase font-black text-[9px] tracking-wider block">Number of Active Drivers / Vehicles</label>
                  <input 
                    type="text"
                    disabled={driverQuoteSubmitting}
                    value={driverQuoteVehicles}
                    onChange={(e) => setDriverQuoteVehicles(e.target.value)}
                    placeholder="e.g. 15-50 vehicles"
                    className="w-full bg-black/60 border border-zinc-900 rounded-xl p-3 text-white text-xs focus:border-orange-500 focus:outline-none transition-all disabled:opacity-50"
                  />
                </div>

                <div className="col-span-1 md:col-span-2 space-y-2">
                  <label className="text-orange-400 uppercase font-black text-[9px] tracking-wider block">Required Custom Features</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-1">
                    {[
                      "Customer Booking Website",
                      "Driver Mobile App",
                      "Dispatch Control Panel",
                      "Live GPS Tracking Map",
                      "Customer ETA Tracking Page",
                      "Automated Route Planner"
                    ].map((feat) => {
                      const isChecked = driverQuoteFeatures.includes(feat);
                      return (
                        <label key={feat} className="flex items-center gap-2 text-[10px] text-slate-350 select-none cursor-pointer bg-black/40 border border-zinc-900 rounded-xl p-2.5 hover:border-orange-500/40 transition-all">
                          <input 
                            type="checkbox"
                            disabled={driverQuoteSubmitting}
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setDriverQuoteFeatures([...driverQuoteFeatures, feat]);
                              } else {
                                setDriverQuoteFeatures(driverQuoteFeatures.filter(f => f !== feat));
                              }
                            }}
                            className="accent-orange-500 w-3.5 h-3.5"
                          />
                          <span>{feat}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="col-span-1 md:col-span-2 space-y-1.5">
                  <label className="text-orange-400 uppercase font-black text-[9px] tracking-wider block">Enquiry Context</label>
                  <input 
                    type="text"
                    readOnly
                    value="Driver / Courier / Fleet Systems"
                    className="w-full bg-black/30 border border-zinc-900/60 rounded-xl p-3 text-slate-500 text-xs select-none focus:outline-none cursor-not-allowed"
                  />
                </div>

                <div className="col-span-1 md:col-span-2 space-y-1.5">
                  <label className="text-orange-400 uppercase font-black text-[9px] tracking-wider block">Message / Project Requirements</label>
                  <textarea 
                    rows={4}
                    disabled={driverQuoteSubmitting}
                    value={driverQuoteMessage}
                    onChange={(e) => setDriverQuoteMessage(e.target.value)}
                    placeholder="Scribe your custom operations workflow, special requirements, database preferences, or deployment timelines..."
                    className="w-full bg-black/60 border border-zinc-950 rounded-xl p-3 text-white text-xs focus:border-orange-500 focus:outline-none transition-all resize-y font-sans disabled:opacity-50"
                  />
                </div>

                {driverQuoteError && (
                  <div className="col-span-1 md:col-span-2 p-4 bg-red-950/20 border border-red-500/30 rounded-2xl text-red-400 text-xs font-sans flex items-center gap-3">
                    <span className="text-xl">⚠</span>
                    <span>{driverQuoteError}</span>
                  </div>
                )}

                <div className="col-span-1 md:col-span-2 pt-2">
                  <button 
                    type="submit"
                    disabled={driverQuoteSubmitting}
                    className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-[0_4px_15px_rgba(249,115,22,0.2)] hover:shadow-[0_4px_25px_rgba(249,115,22,0.35)] cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {driverQuoteSubmitting ? "Sending request…" : "Submit Custom System Request"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* MODAL: FULL REFUND POLICY DETAILS */}
      <AnimatePresence>
        {showFullRefundModal && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-w-md w-full bg-[#050914] border-2 border-cyan-500/40 rounded-3xl p-6 text-left space-y-4 shadow-[0_0_50px_rgba(0,255,255,0.2)]"
            >
              <div className="flex justify-between items-center border-b border-slate-900 pb-3">
                <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider flex items-center gap-1.5">
                  <ShieldCheck className="text-cyan-400 w-4 h-4" />
                  Full Refund Policy
                </h4>
                <button 
                  onClick={() => setShowFullRefundModal(false)}
                  className="p-1 bg-slate-950 border border-slate-800 text-slate-500 hover:text-white rounded-lg transition-colors cursor-pointer"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="space-y-3 font-sans text-xs text-slate-300 leading-relaxed max-h-[300px] overflow-y-auto normal-case pr-1">
                <p>
                  This document establishes the official refund protocol for NexoraX Digital Ltd core operations and direct transactional structures.
                </p>
                
                <h5 className="font-mono text-[9px] font-black text-cyan-400 uppercase mt-2">1. Access Passes & Custom Services</h5>
                <p>
                  Access Passes represent ready-to-run container credits. Once a pass is purchased and the telemetry dashboard initialized, all immediate hardware provisions are finalized. Refund disbursements are limited once access or task deployment has actively started.
                </p>

                <h5 className="font-mono text-[9px] font-black text-cyan-400 uppercase mt-2">2. Monthly Recurring Subscriptions</h5>
                <p>
                  Customers can disable future recurring billings easily. When cancelled, the customer maintains direct entry permissions until the close of the current paid term. No retrospective splits apply.
                </p>

                <h5 className="font-mono text-[9px] font-black text-cyan-400 uppercase mt-2">3. Yearly Long-Term Subscriptions</h5>
                <p>
                  Yearly memberships enjoy premium discounts. Cancellation before the annual term close splits remaining segments under the direct review of the NexoraX legal audit board.
                </p>

                <h5 className="font-mono text-[9px] font-black text-cyan-400 uppercase mt-2">4. Setup Fees & Custom Deployments</h5>
                <p>
                  Setup fees and advanced system deployments are strictly non-refundable once the target Docker configuration is initialized or engineer hours are spent.
                </p>
              </div>

              <button 
                onClick={() => setShowFullRefundModal(false)}
                className="w-full py-2.5 bg-[#00ffff] hover:bg-[#00ebd4] text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-[0_0_15px_rgba(0,255,255,0.3)] cursor-pointer"
              >
                Close Refund Document
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL: FULL CANCELLATION POLICY DETAILS */}
      <AnimatePresence>
        {showFullCancelModal && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-w-md w-full bg-[#050914] border-2 border-[#e840f3]/40 rounded-3xl p-6 text-left space-y-4 shadow-[0_0_50px_rgba(232,64,243,0.2)]"
            >
              <div className="flex justify-between items-center border-b border-slate-900 pb-3">
                <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider flex items-center gap-1.5">
                  <X className="text-[#e840f3] w-4 h-4" />
                  Full Cancellation Policy
                </h4>
                <button 
                  onClick={() => setShowFullCancelModal(false)}
                  className="p-1 bg-slate-950 border border-slate-800 text-slate-500 hover:text-white rounded-lg transition-colors cursor-pointer"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="space-y-3 font-sans text-xs text-slate-300 leading-relaxed max-h-[300px] overflow-y-auto normal-case pr-1">
                <p>
                  This terms policy dictates active system deactivation criteria across the NexoraX NexoraX Ecosystem.
                </p>

                <h5 className="font-mono text-[9px] font-black text-purple-400 uppercase mt-2">1. Request Initiation</h5>
                <p>
                  Any partner or registered enterprise lead can submit a direct deactivation notice to Enterprise Support desks. Submit standard requests directly via support channels at support@nexoraxdigital.co.uk.
                </p>

                <h5 className="font-mono text-[9px] font-black text-purple-400 uppercase mt-2">2. System Deactivation Trigger</h5>
                <p>
                  Writers, builders, active automation loops, and custom API sync partitions remain active through the final hours of the paid service schedule. After transition closure, target infrastructure is archived.
                </p>

                <h5 className="font-mono text-[9px] font-black text-purple-400 uppercase mt-2">3. Historical Data Downloads</h5>
                <p>
                  Following cancellation approval, operators can download structural data backups regarding customer analytics and user metrics files before server deletion.
                </p>
              </div>

              <button 
                onClick={() => setShowFullCancelModal(false)}
                className="w-full py-2.5 bg-[#e840f3] hover:bg-[#db35e6] text-white font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-[0_0_15px_rgba(232,64,243,0.3)] cursor-pointer"
              >
                Close Cancellation Document
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL: TIDE QR PORTAL */}
      {selectedQrInfo && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/95 backdrop-blur-md">
          <div className="bg-[#0b0f19] border-2 border-cyan-500/50 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-[0_0_50px_rgba(0,255,255,0.25)] relative overflow-hidden flex flex-col items-center">
            
            {/* Close Button */}
            <button 
              onClick={() => setSelectedQrInfo(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors cursor-pointer"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Title */}
            <h3 className="font-sans font-black text-white text-lg tracking-tight uppercase mb-1 text-center">
              {selectedQrInfo.title}
            </h3>
            
            {/* Price */}
            <span className="font-mono text-cyan-400 font-extrabold text-sm uppercase tracking-wider mb-6 text-center">
              £{typeof selectedQrInfo.price === 'number' ? selectedQrInfo.price.toFixed(2).replace('.00', '') : selectedQrInfo.price} Secure Tide Payment
            </span>

            {/* QR Image Container */}
            <div className="relative group rounded-2xl overflow-hidden border border-slate-800 bg-black p-2 mb-6 max-w-[280px] w-full aspect-square flex items-center justify-center">
              <img 
                src={selectedQrInfo.image} 
                alt={`${selectedQrInfo.title} Tide QR Code`}
                className="w-full h-full object-contain rounded-xl"
              />
            </div>

            {/* Instruction */}
            <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest text-center leading-relaxed max-w-xs mb-6">
              Scan with your phone camera or banking app to pay securely
            </p>

            {/* Action Buttons */}
            {selectedQrInfo.url && (
              <button
                onClick={() => {
                  window.open(selectedQrInfo.url, '_blank', 'noopener,noreferrer');
                }}
                className="w-full py-3 bg-gradient-to-r from-cyan-400 to-[#e840f3] text-black font-mono text-[10px] font-black uppercase tracking-widest rounded-xl transition-all hover:scale-[1.02] cursor-pointer text-center"
              >
                Open secure Tide payment page
              </button>
            )}

            <button
              onClick={() => setSelectedQrInfo(null)}
              className="w-full py-2.5 bg-transparent text-slate-500 hover:text-white font-mono text-[9px] uppercase tracking-wider mt-3 cursor-pointer text-center"
            >
              Close Portal
            </button>

          </div>
        </div>
      )}

    </div>
  );
}
